<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>


    <card class="no-scrollbar" style="overflow-x: scroll">
      <div class="tabs-header d-flex align-items-end mb-3">
        <tab-rail
          :tabs="categories"
          :value="activeTabIndex"
          @input="switchTab"
          class="flex-grow-1"
        />
        <div class="tabs-header__action d-sm-flex d-none" v-if="activeTabIndex === 0 && !add_promo_code && !edit_promo_code && !bulk_update">
          <base-button @click="bulkUpdatePrompt()" link type="primary" size="sm">
            Bulk actions </base-button>
          <base-button v-on:click="addNewPromo()" type="primary" size="sm">
            <i class="tim-icons mr-1 el-icon-plus"></i> Create new promo code</base-button>
        </div>
        <div class="tabs-header__action d-sm-none d-flex" v-if="activeTabIndex === 0 && !add_promo_code && !edit_promo_code && !bulk_update">
          <base-button @click="bulkUpdatePrompt()" type="primary" size="sm" title="Bulk actions">
            <i class="tim-icons icon-bullet-list-67"></i></base-button>
          <base-button v-on:click="addNewPromo()" type="primary" size="sm">
            <i class="tim-icons el-icon-plus"></i></base-button>
        </div>
        <div class="tabs-header__action d-sm-flex d-none" v-if="activeTabIndex === 2">
          <base-button v-on:click="handleCreateTemplate()" type="primary" size="sm">
            <i class="tim-icons mr-1 el-icon-plus"></i> Create new promo code group</base-button>
        </div>
        <div class="tabs-header__action d-sm-none d-flex" v-if="activeTabIndex === 2">
          <base-button v-on:click="handleCreateTemplate()" type="primary" size="sm">
            <i class="tim-icons el-icon-plus"></i></base-button>
        </div>
      </div>

      <div v-if="activeTabIndex === 0 || activeTabIndex === 1">
        <!--      Filter bar -->
        <div v-if="!add_promo_code && !edit_promo_code && !bulk_update">
          <filter-bar
            ref="filterBar"
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="promo-codes"
            mobile-collapsible
            :sort-options="sortOptions"
            :sort-value="sort"
            :show-sort-chip="isMobile"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
            @update:sort="onSortChange"
          >
            <template #actions>
              <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>
        </div>

        <data-table
          v-if="!add_promo_code && !edit_promo_code && !bulk_update"
          class="mt-3"
          :columns="promoColumns"
          :rows="promo_codes"
          row-key="id"
          :loading="loading"
          :page="currentPage"
          :per-page="perPage"
          :total="totalRows"
          :sort-value="sort"
          :default-sort="{ key: 'created', dir: 'desc' }"
          @update:sort="onSortChange"
          @update:page="onPageChange"
        >
          <template #cell(name)="{ row }">
            <div>{{row.name}}</div>
            <badge type="danger" v-if="row.internal_only">internal only</badge>
            <badge type="default" v-if="row.campaign_id">issued by campaign</badge>
            <div>
              <small>{{row.description}}</small>
            </div>
          </template>
          <template #cell(group)="{ row }">
            <badge type="info" v-if="row.template_name">{{row.template_name}}</badge>
            <span v-else>-</span>
          </template>
          <template #cell(value)="{ row }">
            <span v-if="!row.redemption_type || (row.redemption_type.hasOwnProperty('id') && row.redemption_type.id === 0)">{{row.value_amount}}%</span>
            <span v-else>{{formatCurrencyValue(row.value_amount)}}</span>
            <span v-if="row.value_type === 0"> per transaction</span>
            <span v-if="row.value_type === 1"> per line item</span>
            <span v-if="row.value_type === 2"> per quantity</span>
          </template>
          <template #cell(applicable_to)="{ row }">
            <template v-if="row.is_cross_group">
              <el-tooltip effect="light" :open-delay="300" placement="bottom-end">
                <template #content>
                  <div style="max-width: 200px; white-space: normal;">
                    Available at all locations under this company
                  </div>
                </template>
                <badge style="cursor: pointer;" type="info">all locations</badge>
              </el-tooltip>
            </template>
            <template v-else>
              <el-tooltip effect="light"
                          :open-delay="300"
                          placement="bottom-end">
                <template #content>
                  <div style="max-width: 200px; white-space: normal;">
                    {{ getPromoLinks(row.linked_games) }}
                  </div>
                </template>
                <badge style="cursor: pointer;" class="ml-2" v-if="JSON.parse(row.linked_games).length > 0" type="default">events</badge>
              </el-tooltip>
              <el-tooltip effect="light"
                          :open-delay="300"
                          placement="bottom-end">
                <template #content>
                  <div style="max-width: 200px; white-space: normal;">
                    {{ getPromoLinks(row.linked_gift_card_types) }}
                  </div>
                </template>
                <badge style="cursor: pointer;" class="ml-2" v-if="JSON.parse(row.linked_gift_card_types).length > 0" type="default">gift cards</badge>
              </el-tooltip>
              <el-tooltip
                effect="light"
                :open-delay="300"
                placement="bottom-end">
                <template #content>
                  <div style="max-width: 200px; white-space: normal;">
                    {{ getPromoLinks(row.linked_items) }}
                  </div>
                </template>
                <badge style="cursor: pointer;" class="ml-2" v-if="JSON.parse(row.linked_items).length > 0" type="default">merchandise</badge>
              </el-tooltip>
              <span v-if="((JSON.parse(row.linked_games).length <= 0)
            && (JSON.parse(row.linked_gift_card_types).length <= 0)
            && (JSON.parse(row.linked_items).length <= 0))">-</span>
            </template>
          </template>
          <template #cell(purchase_start)="{ row }">
            <span v-if="row.purchase_is_range">{{getDate(row.purchase_start_date)}} - {{getDate(row.purchase_end_date)}}</span>
            <span v-else>Available any dates</span>
          </template>
          <template #cell(event_start)="{ row }">
            <span v-if="row.event_is_date_range">{{getDate(row.event_start_date)}} - {{getDate(row.event_end_date)}}</span>
            <span v-else>Available any dates</span>
          </template>
          <template #cell(event_time)="{ row }">
            <span v-if="row.event_is_time_range">{{getTime(row.event_start_time)}} - {{getTime(row.event_end_time)}}</span>
            <span v-else>Available any times</span>
          </template>
          <template #cell(remaining)="{ row }">
            <span v-if="row.quantity >= 0">{{ Math.max(0,row.quantity - row.used)}}</span>
            <span v-else>Unlimited</span>
          </template>
          <template #cell(used)="{ row }">
            <span>{{row.used}}</span>
          </template>
          <template #actions="{ row }">
            <el-dropdown>
              <base-button
                link
                type="primary"
                size="sm"
              > <i style="font-size: large" class="tim-icons el-icon-more"></i>
              </base-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <!-- Edit - only for owned promos (not cross-group from other locations) -->
                  <a v-if="!row.is_cross_group || isPromoFromCurrentGroup(row)"
                     class="dropdown-item mt-1 mb-2" href="#"
                     v-on:click="editPromoCode(row, $event)">
                    <i class="tim-icons text-default icon-pencil mr-2"></i> Edit promo code
                  </a>
                  <!-- View only - for cross-group promos from other locations -->
                  <a v-if="row.is_cross_group && !isPromoFromCurrentGroup(row)"
                     class="dropdown-item mt-1 mb-2" href="#"
                     v-on:click="viewPromoCode(row, $event)">
                    <i class="tim-icons text-default icon-zoom-split mr-2"></i> View promo code
                  </a>
                  <a v-if="row.used > 0" class="dropdown-item mt-1 mb-2"
                     href="#"
                     v-on:click="getTransactions(row, $event)">
                    <i class="tim-icons text-default icon-notes mr-2"></i> View associated transactions
                  </a>
                  <!-- Apply group settings - shown for owned promos in a group; disabled when the group has no defaults -->
                  <a v-if="archived === 0 && row.template_id && (!row.is_cross_group || isPromoFromCurrentGroup(row))"
                     class="dropdown-item mt-1 mb-2"
                     :class="{ 'dropdown-item-na': !promoGroupHasDefaults(row) }"
                     :title="promoGroupHasDefaults(row) ? null : 'This promo code\'s group has no default settings to apply'"
                     href="#"
                     v-on:click="promoGroupHasDefaults(row) ? applyGroupSettingsToPromo(row, $event) : $event.preventDefault()">
                    <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Apply group settings
                  </a>
                  <!-- Copy to groups - only for owned promos -->
                  <a v-if="!row.is_cross_group || isPromoFromCurrentGroup(row)"
                     class="dropdown-item mt-1 mb-2" href="#"
                     v-on:click="copyToGroupsPrompt(row, $event)">
                    <i class="tim-icons text-default icon-single-copy-04 mr-2"></i> Copy to other company groups
                  </a>
                  <hr v-if="!row.is_cross_group || isPromoFromCurrentGroup(row)">
                  <!-- Archive - only for owned promos -->
                  <a v-if="archived === 0 && (!row.is_cross_group || isPromoFromCurrentGroup(row))"
                     v-on:click="deletePromoCodePrompt(row, $event)"
                     class="dropdown-item mt-1 mb-2" href="#">
                    <i class="tim-icons text-default icon-trash-simple mr-2"></i> Archive promo code
                  </a>
                  <a v-if="archived === 1 && (!row.is_cross_group || isPromoFromCurrentGroup(row))"
                     v-on:click="unarchivePromoCodePrompt(row, $event)"
                     class="dropdown-item mt-1 mb-2" href="#">
                    <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Unarchive promo code
                  </a>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
          <template #empty>
            <p class="data-table__empty-text">You have no promo codes.</p>
          </template>
        </data-table>

        <div v-if="add_promo_code || edit_promo_code || bulk_update" :key="un_key">
          <div class="row" v-if="bulk_update">
            <div class="col">
              <p class="text-uppercase card-title">Promo codes to bulk update</p>
            </div>
          </div>

          <div class="row mb-2" v-if="bulk_update">
            <div class="col-lg-6">
              <div v-if="selectAllPromoCodesFlag" style="width: 250px" class="d-flex align-items-center mt-2 p-2 border border-primary rounded mb-3">
                <i class="text-white fas fa-info-circle mr-2"></i>
                <small class="text-white">
                  All {{ all_promos.length }} promo codes selected
                </small>
              </div>
              <div v-else>
                <div class="mb-3">
                  <label class="text-white mb-2">Selection method:</label>
                  <div>
                    <div class="form-check form-check-radio">
                      <label class="form-check-label">
                        <input
                          class="form-check-input"
                          type="radio"
                          value="group"
                          v-model="bulkSelectionMethod"
                          @change="bulk_promo_codes = null"
                        />
                        <span class="form-check-sign"></span>
                        <span class="text-white">By promo code group</span>
                      </label>
                    </div>
                    <div class="form-check form-check-radio">
                      <label class="form-check-label">
                        <input
                          class="form-check-input"
                          type="radio"
                          value="individual"
                          v-model="bulkSelectionMethod"
                          @change="bulk_promo_code_groups = []; bulk_promo_codes = null"
                        />
                        <span class="form-check-sign"></span>
                        <span class="text-white">Individual promo codes</span>
                      </label>
                    </div>
                  </div>
                </div>

                <base-input v-if="bulkSelectionMethod === 'group'" label="Select promo code groups" :error="errs.bulk_promos">
                  <el-select multiple clearable filterable
                             v-model="bulk_promo_code_groups"
                             class="select-primary"
                             placeholder="Select promo code groups"
                             value-key="id"
                             @change="handleBulkGroupSelection"
                  >
                    <el-option
                      v-for="group in availablePromoCodeGroups"
                      class="select-primary"
                      :label="group.name"
                      :value="{id: group.id, name: group.name}"
                      :key="group.id"
                    ></el-option>
                  </el-select>
                </base-input>

                <base-input v-if="bulkSelectionMethod === 'individual'" label="Select promo codes" :error="errs.bulk_promos">
                  <el-select multiple clearable filterable
                             v-model="bulk_promo_codes"
                             class="select-primary"
                             name="value_type"
                             placeholder="Promo codes"
                             value-key="id"
                  >
                    <el-option
                      v-for="(p, index) in all_promos"
                      class="select-primary"
                      :label="p.name"
                      :value="{id: p.id, name: p.name}"
                      :key="index + 'PRP'"
                    ></el-option>
                  </el-select>
                </base-input>

                <div v-if="bulk_promo_codes && bulk_promo_codes.length > 0" class="mt-1 mb-3">
                  <small class="text-white">
                    <i class="tim-icons icon-check-2 text-success mr-2"></i>
                    {{ bulk_promo_codes.length }} promo code(s) selected
                  </small>
                </div>
              </div>

              <el-switch active-color="#e14eca"
                         v-model="selectAllPromoCodesFlag" />
              <label class="text-white"> &nbsp; Select all promo codes</label>
            </div>
          </div>

          <div class="row mb-3" v-if="bulk_update">
            <div class="col-lg-6">
              <base-input label="Fields to bulk update" :error="errs.bulk_fields">
                <el-select multiple clearable
                           v-model="selected_bulk_update_fields"
                           class="select-primary"
                           name="value_type"
                           placeholder="Fields"
                           value-key="id"
                           :disabled="applyDefaultSettingsFromGroup"
                >
                  <el-option
                    v-for="(field, index) in bulk_update_fields"
                    class="select-primary"
                    :label="field.name"
                    :value="{id: field.id, name: field.name}"
                    :key="index + 'BUF'"
                  ></el-option>
                </el-select>
              </base-input>
              <base-button v-if="!applyDefaultSettingsFromGroup" style="padding: 0px 0px 0px 0px" link type="primary" v-on:click="addAllFields()">
                <i class="tim-icons icon-simple-add mr-1"></i> Add all fields</base-button>
              <small v-if="applyDefaultSettingsFromGroup" class="text-white">Field selection disabled when applying default settings from group</small>
            </div>
          </div>

          <div v-if="view_only" class="alert alert-info mb-3">
            <i class="tim-icons icon-alert-circle-exc mr-2"></i>
            This promo code was created at <strong>{{ getOriginGroupName() }}</strong>. You can view but not edit it.
          </div>
          <div class="form-section" v-if="!bulk_update ||
         (bulk_update && (bulkUpdateFieldSelected('Description') || bulkUpdateFieldSelected('Promo code group')))">
            <p class="form-section-title">Promo code details</p>
          <div class="row">
            <div class="col-lg-6">
              <div class="row" v-if="!bulk_update">
                <div class="col">
                  <label class="text-white">Promo code *
                    <el-popover trigger="hover"
                                placement="right">

                      <div>
                        <div class="popover-body">This is a unique code customers will use to apply the discount.<br/> Promo codes must be alphanumeric.</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <base-input required
                              :error="errs.name"
                              :disabled="view_only"
                              v-model="promo.name" placeholder="Code"></base-input>
                </div>
              </div>
              <div class="row" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Description'))">
                <div class="col">
                  <base-input label="Description">
                    <el-input style="background-color: inherit"
                              v-model="promo.description"
                              :rows="5"
                              type="textarea"
                              :disabled="view_only"
                              placeholder="Promotion description"
                    />
                  </base-input>
                </div>
              </div>
              <div class="row" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Promo code group'))">
                <div class="col">
                  <base-input :label="bulk_update ? 'Promo code group' : 'Promo code group (optional)'">
                    <el-select
                      clearable
                      v-model="promo.template_id"
                      class="select-primary"
                      name="template_id"
                      placeholder="Select promo code group"
                      value-key="id"
                      @change="handleTemplateSelection"
                    >
                      <el-option
                        v-for="group in availablePromoCodeGroups"
                        class="select-primary"
                        :label="group.name"
                        :value="group.id"
                        :key="group.id"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
              <div class="row mt-1 mb-3" v-if="(add_promo_code || edit_promo_code) && !bulk_update && selectedGroupHasDefaultSettings">
                <div class="col">
                  <base-alert type="info" class="mb-3">
                    <i class="tim-icons icon-alert-circle-exc mr-2"></i>
                    <small>This group has default settings configured. Enable the toggle below to apply these settings. This will overwrite the current settings for this promo code.</small>
                  </base-alert>
                  <div class="d-flex align-items-center">
                    <el-switch active-color="#e14eca" v-model="applyTemplateOnEdit" @change="handleApplyTemplateOnEditToggle" :disabled="loadingTemplateSettings" />
                    <label class="text-white mb-0 ml-2">Apply default settings from group</label>
                    <i v-if="loadingTemplateSettings" class="el-icon-loading ml-2"></i>
                  </div>
                </div>
              </div>
              <div class="row" v-if="bulk_update && bulkUpdateFieldSelected('Promo code group') && selectedGroupHasDefaultSettings">
                <div class="col">
                  <base-alert type="info" class="mb-2">
                    <i class="tim-icons icon-alert-circle-exc mr-2"></i>
                    <small>This group has default settings configured. Enable the toggle below to apply these settings. This will overwrite any existing settings on the selected promo codes.</small>
                  </base-alert>
                  <el-switch active-color="#e14eca" v-model="applyDefaultSettingsFromGroup" @change="handleApplyDefaultSettingsToggle" />
                  <label class="text-white"> &nbsp; Apply default settings from group</label>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div class="form-section" v-if="!bulk_update ||
         (bulk_update &&
          (bulkUpdateFieldSelected('Redemption type')
          || bulkUpdateFieldSelected('Value type')
          || bulkUpdateFieldSelected('Amount')
          || bulkUpdateFieldSelected('Quantity')))">
            <p class="form-section-title">Redemption rules</p>
            <base-alert v-if="hasBuyXGetYRules && !bulk_update" type="info" class="mb-3 fit-content">
              <i class="tim-icons icon-bulb-63 mr-2"></i>
              This discount applies in addition to Buy X Get Y rules. Set amount to <strong>0</strong> for rule-based discount only.
            </base-alert>
          <div class="row">
            <div class="col">
              <!-- Redemption type and Value type row -->
              <div class="row">
                <div class="col-lg-3 col-md-4 col-sm-6" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Redemption type'))">
                  <base-input label="Redemption type">
                    <el-select
                      v-model="promo.redemption_type"
                      class="select-primary"
                      name="redemption_type"
                      placeholder="Redemption type"
                      value-key="id"
                      :disabled="view_only"
                    >
                      <el-option
                        v-for="(type, index) in redemptionTypes"
                        class="select-primary"
                        :label="type.name"
                        :value="{name: type.name, id: type.id}"
                        :key="index + 'R'"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
                <div class="col-lg-3 col-md-4 col-sm-6" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Value type'))">
                  <base-input label="Value type">
                    <el-select
                      v-model="promo.value_type"
                      class="select-primary"
                      name="value_type"
                      placeholder="Value type"
                      value-key="id"
                      :disabled="view_only"
                    >
                      <el-option
                        v-for="(type, index) in valueTypes"
                        class="select-primary"
                        :label="type.id === 1 ? type.name + ' (Recommended)' : type.name"
                        :value="{name: type.name, id: type.id}"
                        :key="index + 'VT'"
                      >
                        <span>{{ type.name }}</span>
                        <span v-if="type.id === 1" class="recommended-badge">Recommended</span>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
              <!-- Info text about discount type -->
              <div class="row mb-3" v-if="promo.value_type && !bulk_update">
                <div class="col">
                  <p class="small mb-0" style="opacity: 0.7;">
                    <i class="tim-icons el-icon-info mr-1"></i>
                    <span v-if="promo.redemption_type.id === 1 && promo.value_type.id === 0">
                      Applies a fixed discount amount once per transaction. Not itemized in line-item reporting.
                    </span>
                    <span v-if="promo.redemption_type.id === 1 && promo.value_type.id === 1">
                      Applies a fixed discount amount to each line item individually. Reflected in line-item reporting.
                    </span>
                    <span v-if="promo.redemption_type.id === 1 && promo.value_type.id === 2">
                      Applies a fixed discount amount per unit of quantity purchased. Reflected in line-item reporting for associated items. In case of bookings, quantity refers to group size.
                    </span>
                    <span v-if="promo.redemption_type.id === 0 && promo.value_type.id === 0">
                      Applies a percentage-based discount to the total transaction amount. Not itemized in line-item reporting.
                    </span>
                    <span v-if="promo.redemption_type.id === 0 && promo.value_type.id === 1">
                      Applies a percentage-based discount to each line item individually. Reflected in line-item reporting.
                    </span>
                    <span v-if="promo.redemption_type.id === 0 && promo.value_type.id === 2">
                      Applies a percentage-based discount per unit of quantity purchased. Reflected in line-item reporting. In case of bookings, quantity refers to group size.
                    </span>
                  </p>
                </div>
              </div>
              <!-- Amount and Quantity row -->
              <div class="row align-items-end" v-if="!bulk_update || (bulk_update && (bulkUpdateFieldSelected('Amount') || bulkUpdateFieldSelected('Quantity')))">
                <div class="col-auto" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Amount'))">
                  <label>Amount</label>
                  <div class="amount-input-group" :class="{ 'has-error': errs.value_amount }">
                    <span class="amount-prefix">{{ promo.redemption_type && promo.redemption_type.id === 0 ? '%' : currency_symbol }}</span>
                    <base-input class="amount-input-wrapper mb-0" style="width: 120px">
                      <el-input-number
                        :controls="false"
                        v-model="promo.value_amount"
                        :min="0"
                        :precision="2"
                        :disabled="view_only"
                        placeholder="0.00"
                      />
                    </base-input>
                  </div>
                  <span v-if="errs.value_amount" class="text-danger small">{{ errs.value_amount }}</span>
                </div>
                <div class="col-auto" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Quantity'))">
                  <label>Quantity</label>
                  <div class="quantity-control">
                    <div class="quantity-toggle" :style="view_only ? 'pointer-events: none; opacity: 0.6;' : ''">
                      <span
                        class="quantity-option"
                        :class="{ 'is-active': promo.is_unlimited }"
                        @click="promo.is_unlimited = true; un_key++"
                      >Unlimited</span>
                      <span
                        class="quantity-option"
                        :class="{ 'is-active': !promo.is_unlimited }"
                        @click="promo.is_unlimited = false; un_key++"
                      >Limited</span>
                    </div>
                    <div v-if="!promo.is_unlimited" class="quantity-input-wrapper">
                      <base-input class="mb-0" style="width: 130px">
                        <el-input-number
                          v-model="promo.quantity"
                          :min="1"
                          :disabled="view_only"
                          size="small"
                        />
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div class="form-section" v-if="!bulk_update ||
         (bulk_update && bulkUpdateFieldSelected('Allowed purchase dates'))">
            <p class="form-section-title">Purchase rules</p>

          <div class="row">
            <div class="col">
              <div class="row">
                <div class="col-lg-3 col-md-4 col-sm-6" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed purchase dates'))">
                  <base-input label="Allowed purchase dates">
                    <el-select
                      v-model="promo.purchase_all_dates"
                      class="select-primary"
                      name="value_type"
                      placeholder="Value type"
                      value-key="id"
                      v-on:change="un_key++"
                    >
                      <el-option
                        v-for="(opt, index) in dateOptions"
                        class="select-primary"
                        :label="opt.name"
                        :value="{id: opt.id, name: opt.name}"
                        :key="index + 'DY'"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>

                <div class="col-lg-3 col-6" v-if="promo.purchase_all_dates.id == 1 &&
                (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed purchase dates')))">
                  <base-input label="Purchase start date" :error="errs.purchase_start_date">
                    <el-date-picker
                      type="date"
                      v-on:change="validateDateRange()"
                      placeholder="mm-dd-yyyy"
                      value-format="yyyy-MM-dd"
                      name="date"
                      v-model="promo.purchase_start_date"
                      :format="date_format_cal"
                      :picker-options="{ firstDayOfWeek: first_day_of_week }"
                    >
                    </el-date-picker>
                  </base-input>
                  <!--                      :format="date_format_cal"-->
                </div>
                <div class="col-lg-3 col-6" v-if="promo.purchase_all_dates.id == 1 &&
                (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed purchase dates')))">
                  <base-input label="Purchase end date" :error="errs.purchase_end_date">
                    <el-date-picker
                      type="date"
                      v-on:change="validateDateRange()"
                      placeholder="mm-dd-yyyy"
                      value-format="yyyy-MM-dd"
                      name="date"
                      v-model="promo.purchase_end_date"
                      :format="date_format_cal"
                      :picker-options="{ firstDayOfWeek: first_day_of_week }"
                    >
                    </el-date-picker>
                  </base-input>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div class="form-section" v-if="!bulk_update ||
         (bulk_update &&
          (bulkUpdateFieldSelected('Can be combined with other promo codes')
          || bulkUpdateFieldSelected('Can only be applied internally')
          || bulkUpdateFieldSelected('Available at all locations')
          || bulkUpdateFieldSelected('Allowed booking dates')
          || bulkUpdateFieldSelected('Allowed booking time')
          || bulkUpdateFieldSelected('Allowed days of the week')
          || bulkUpdateFieldSelected('Exclusion dates')
          || bulkUpdateFieldSelected('Applicable to')))">
            <p class="form-section-title">Usage and application rules</p>

          <div class="row"
               v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Can be combined with other promo codes'))">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="promo.is_combineable"
                         :disabled="view_only" />
              <label> &nbsp; Can be combined with other promo codes</label>
            </div>
          </div>
          <div class="row"
               v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Can only be applied internally'))">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="promo.internal_only"
                         :disabled="view_only" />
              <label> &nbsp; Can only be applied internally</label>
            </div>
          </div>
          <div class="row"
               v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Available at all locations'))">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="promo.is_cross_group"
                         :disabled="view_only"
                         @change="handleCrossGroupToggle" />
              <label> &nbsp; Available at all locations</label>
              <el-tooltip effect="light" placement="right" :open-delay="300">
                <template #content>
                  <div style="max-width: 280px;">
                    When enabled, this promo code can be used at any of your locations
                    and will apply to all eligible games, items, and gift cards.
                  </div>
                </template>
                <i class="tim-icons el-icon-info ml-2" style="color: #1d8cf8; cursor: help;"></i>
              </el-tooltip>
            </div>
          </div>

          <div class="row mt-2" :key="un_key"
               v-if="(!bulk_update || (bulk_update && bulkUpdateFieldSelected('Applicable to'))) && !promo.is_cross_group">
            <div class="col">
              <div class="row">
                <div class="col-lg-6">
                  <base-input label="Applicable to">
                    <el-select multiple clearable
                               v-model="promo.purchasable_items"
                               class="select-primary"
                               name="value_type"
                               placeholder="Items"
                               value-key="uid"
                               v-on:change="un_key++"
                    >
                      <el-option
                        v-for="(item, index) in applicableToOptions"
                        class="select-primary"
                        :label="item.displayLabel"
                        :value="{id: item.id, name: item.name, item_type: item.item_type, uid: item.uid}"
                        :key="index + 'G'"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
              <div class="quick-actions">
                <span class="quick-actions-label">Quick add:</span>
                <base-button style="padding: 0px" link type="primary" size="sm" @click="addAllPurchaseTypes(purchasable_items, 'game')">All games</base-button>
                <base-button style="padding: 0px" link type="primary" size="sm" @click="addAllPurchaseTypes(purchasable_items, 'item')">All items</base-button>
                <base-button style="padding: 0px" link type="primary" size="sm" @click="addAllPurchaseTypes(purchasable_items, 'gift_card_type')">All gift card types</base-button>
              </div>
            </div>
          </div>

          <div class="row mt-3" v-if="appliesToGames() || bulk_update">
            <div class="col">
              <div class="row">
                <div class="col-lg-3 col-md-4 col-sm-6" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking dates'))">
                  <base-input label="Allowed booking dates">
                    <el-select
                      v-model="promo.event_all_dates"
                      class="select-primary"
                      name="value_type"
                      placeholder="Booking date"
                      value-key="id"
                      v-on:change="un_key++"
                    >
                      <el-option
                        v-for="(opt, index) in dateOptions"
                        class="select-primary"
                        :label="opt.name"
                        :value="{id: opt.id, name: opt.name}"
                        :key="index + 'DX'"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>


                <div class="col-lg-3 col-6"
                     v-if="promo.event_all_dates.id == 1
                   && (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking dates')))" >
                  <base-input label="Event start date" :error="errs.event_start_date">
                    <el-date-picker
                      type="date"
                      v-on:change="validateDateRange()"
                      placeholder="mm-dd-yyyy"
                      value-format="yyyy-MM-dd"
                      name="date"
                      v-model="promo.event_start_date"
                      :format="date_format_cal"
                      :picker-options="{ firstDayOfWeek: first_day_of_week }"
                    >
                    </el-date-picker>
                  </base-input>
                </div>
                <div class="col-lg-3 col-6" v-if="promo.event_all_dates.id == 1
                   && (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking dates')))" >
                  <base-input label="Event end date" :error="errs.event_end_date">
                    <el-date-picker
                      type="date"
                      v-on:change="validateDateRange()"
                      placeholder="mm-dd-yyyy"
                      value-format="yyyy-MM-dd"
                      name="date"
                      v-model="promo.event_end_date"
                      :format="date_format_cal"
                      :picker-options="{ firstDayOfWeek: first_day_of_week }"
                    >
                    </el-date-picker>
                  </base-input>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-3 col-md-4 col-sm-6">
                  <base-input label="Allowed booking time" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking time'))">
                    <el-select
                      v-model="promo.event_all_times"
                      class="select-primary"
                      name="value_type"
                      placeholder="Time option"
                      value-key="id"
                      v-on:change="un_key++"
                    >
                      <el-option
                        v-for="(opt, index) in timeOptions"
                        class="select-primary"
                        :label="opt.name"
                        :value="{id: opt.id, name: opt.name}"
                        :key="index + 'XT'"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
                <div class="col-lg-3 col-6"
                     v-if="promo.event_all_times.id == 1 &&
                   (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking time')))">
                  <base-input label="Event start time" :error="errs.event_start_time">
                    <el-time-picker v-on:change="validateDateRange()"
                                    v-model="promo.event_start_time"
                                    :format="picker_time_format" placeholder="HH:MM"
                    ></el-time-picker>
                  </base-input>
                </div>
                <div class="col-lg-3 col-6" v-if="promo.event_all_times.id == 1 &&
                   (!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed booking time')))">
                  <base-input label="Event end time" :error="errs.event_end_time">
                    <el-time-picker v-on:change="validateDateRange()"
                                    v-model="promo.event_end_time"
                                    :format="picker_time_format" placeholder="HH:MM"
                    ></el-time-picker>
                  </base-input>
                </div>
              </div>
              <div v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Allowed days of the week'))">
                <label class="mb-2">Allowed days of the week</label>
                <div class="days-selector">
                  <div class="day-pill" :class="{ 'is-selected': promo.available_monday }" @click="promo.available_monday = !promo.available_monday">Mon</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_tuesday }" @click="promo.available_tuesday = !promo.available_tuesday">Tue</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_wednesday }" @click="promo.available_wednesday = !promo.available_wednesday">Wed</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_thursday }" @click="promo.available_thursday = !promo.available_thursday">Thu</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_friday }" @click="promo.available_friday = !promo.available_friday">Fri</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_saturday }" @click="promo.available_saturday = !promo.available_saturday">Sat</div>
                  <div class="day-pill" :class="{ 'is-selected': promo.available_sunday }" @click="promo.available_sunday = !promo.available_sunday">Sun</div>
                </div>
              </div>
              <div class="row mt-3" v-if="!bulk_update || (bulk_update && bulkUpdateFieldSelected('Exclusion dates'))">
                <div class="col-lg-3 col-md-4 col-sm-6">
                  <base-input label="Exclusion dates">
                    <el-date-picker
                      type="dates"
                      placeholder="mm-dd-yyyy"
                      value-format="yyyy-MM-dd"
                      name="date"
                      :format="date_format_cal"
                      v-model="promo.event_exlusions"
                      :picker-options="{ firstDayOfWeek: first_day_of_week }"
                    >
                    </el-date-picker>
                  </base-input>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div class="form-section" v-if="(add_promo_code || edit_promo_code) && !bulk_update">
            <p class="form-section-title">Eligibility rules</p>
            <promo-rules-manager
              :rules="promoRules"
              :available-rule-types="availableRuleTypes"
              :available-games="getItemsByType('game')"
              :available-items="getItemsByType('item')"
              :available-gift-cards="getItemsByType('gift_card_type')"
              :value-type="promo.value_type?.id ?? 0"
              :currency-symbol="currency_symbol"
              @add-rule="handleAddRule"
              @update-rule="handleUpdateRule"
              @remove-rule="handleRemoveRule"
            />
          </div>

          <div class="form-section" v-if="!bulk_update">
            <p class="form-section-title">Duplication</p>
            <div class="row">
              <div class="col-lg-3 col-md-4 col-sm-6">
                <base-input label="Duplicate to other company groups">
                  <el-select multiple
                             v-model="selectedGroups"
                             class="select-primary"
                             name="value_type"
                             placeholder="Groups"
                             value-key="id"
                  >
                    <el-option
                      v-for="(group, i) in groups"
                      class="select-primary"
                      :label="group.name"
                      :value="{ name: group.name, id: group.id} "
                      :key="'FN' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </div>



          <div class="row mt-2" v-if="add_promo_code">
            <div class="col">
              <base-button v-on:click="add_promo_code = false" type="default">
                Cancel
              </base-button>
              <base-button class="ml-2" v-on:click="createPromoCode()" type="primary">
                Create
              </base-button>
            </div>
          </div>
          <div class="row mt-2" v-if="edit_promo_code">
            <div class="col">
              <base-button v-on:click="cancelEdit(promo)" type="default">
                {{ view_only ? 'Close' : 'Cancel' }}
              </base-button>
              <base-button v-if="!view_only" class="ml-2" v-on:click="savePromoCode(promo)" type="primary">
                Update
              </base-button>
            </div>
          </div>

          <div class="row mt-2" v-if="bulk_update">
            <div class="col">
              <base-button v-on:click="bulk_update = false" type="default">
                Cancel
              </base-button>
              <base-button class="ml-2" v-on:click="bulkUpdatePromoCodes()" type="primary">
                Update
              </base-button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="activeTabIndex === 2">
        <promo-code-templates
          ref="promoCodeTemplates"
          @create-template="handleCreateTemplate"
          @edit-template="handleEditTemplate"
          @create-from-template="handleCreateFromTemplate"
        ></promo-code-templates>
      </div>

    </card>

    <!--    View associated transactions-->
    <modal :show.sync="modals.promo_transaction"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Transactions with <span class="text-primary">{{selectedPromoCode.name}}</span> applied</h4>

        <div class="row">
          <div class="col">
            <b-table :items="promo_transactions" :fields="['customer', 'transaction']" :per-page="perPagePromos"
                     :current-page="currentPagePromos">
              <template #head(transaction)="data">
                <span></span>
              </template>
              <template #head(customer)="data">
                <span></span>
              </template>
              <template v-slot:cell(customer)="row">
                <h4>{{row.item.first_name}} {{row.item.last_name}}</h4>
                <small>
                  <i class="tim-icons icon-email-85"></i> {{row.item.email}}
                  <i v-if="row.item.phone" class="tim-icons el-icon-mobile-phone"></i> {{row.item.phone}}
                </small>
              </template>
              <template v-slot:cell(transaction)="row">
                <p class="text-white">Transaction #{{row.item.order_number}}
                  <el-tooltip content="View transaction"
                              effect="light"
                              :open-delay="300"
                              placement="top">
                    <base-button v-on:click="goToTransaction(row.item.id)" icon class="btn btn-link" simple size="sm" type="primary">
                      <i class="tim-icons el-icon-top-right"></i>
                    </base-button>
                  </el-tooltip>
                </p>
                <small>{{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}</small>
              </template>
            </b-table>
            <b-pagination
              v-model="currentPagePromos"
              :total-rows="totalRowsPromos"
              :per-page="perPagePromos"
            ></b-pagination>
          </div>
        </div>

        <div class="row text-center">
          <div class="col">
            <base-button type="default" @click="modals.promo_transaction = false">Return</base-button>
          </div>
        </div>
      </card>
    </modal>

    <!--    Copy to other groups-->
    <modal :show.sync="modals.copy_to_groups"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Copy <span class="text-primary">{{selectedPromoCode.name}}</span> to other groups</h4>

        <div class="row mb-4" v-if="groups">
          <div class="col">
            <base-input label="Select groups" :error="errs.groups">
              <el-select multiple
                         v-model="selectedGroups"
                         class="select-primary"
                         name="game"
                         placeholder="Group"
                         value-key="id"
              >
                <el-option
                  v-for="(group, i) in groups"
                  class="select-primary"
                  :label="group.name"
                  :value="{ name: group.name, id: group.id} "
                  :key="'F' + i"
                ></el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row text-center">
          <div class="col">
            <base-button type="default" @click="modals.copy_to_groups = false">Cancel</base-button>
            <base-button type="primary" class="ml-2" @click="copyPromoCode()">Copy</base-button>
          </div>
        </div>

      </card>
    </modal>

    <base-alert-dialog
      :show="confirmationDialog.show"
      :title="confirmationDialog.title"
      :message="confirmationDialog.message"
      :variant="confirmationDialog.variant"
      :show-cancel="confirmationDialog.showCancel"
      :confirm-text="confirmationDialog.confirmText"
      :confirm-variant="confirmationDialog.confirmVariant"
      @confirm="confirmationDialogConfirm"
      @cancel="confirmationDialogCancel"
    />

  </div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Auth} from "aws-amplify";
import BaseAlert from "@/components/BaseAlert";
import TabRail from "@/components/TabRail.vue";
import { downloadCsv } from "@/util/listPage";
import Badge from "@/components/Badge";
import {Dropdown, DropdownMenu, Switch, TimePicker} from "element-ui";
import {BPagination, BTable} from "bootstrap-vue";
import _ from "lodash";
import currencies from "@/assets/currencies.json";
import {formattedDate, time} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import PromoCodeTemplates from './PromoCodeTemplates.vue'
import PromoRulesManager from './PromoRules/PromoRulesManager.vue';

// Filter values for the list's <filter-bar>, one key per filterSchema entry.
// template_name = array of promo code group ids.
function emptyPromoFilters() {
  return {
    name: '',
    template_name: [],
    generated: false,
    value_kind: [],
    value_amount: { from: '', to: '' },
    value_type: [],
    applies_to: [],
    purchase_dates: { mode: 'range', days: '', from: '', to: '' },
    event_dates: { mode: 'range', days: '', from: '', to: '' },
    event_times: { from: '', to: '' },
    remaining: { from: '', to: '', unlimited: false },
    used: { from: '', to: '' }
  }
}

export default {
  name: "PromoCodes",
  data() {
    return {
      loading: false,
      activeTabIndex: 0,
      promo_codes: [],
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      // Server sort for the list; key is a get-promos-for-group sort key. Seeded from the URL in mounted.
      sort: { key: 'created', dir: 'desc' },
      isMobile: false,
      // SavedViewsMenu (FilterBar) drives the first fetch via its initial change; this guards it.
      didInitialFetch: false,
      // True once the live promo code groups have loaded, so the group filter can be
      // checked against them.
      promoGroupsLoaded: false,
      currentPagePromos: 1,
      totalRowsPromos: 0,
      perPagePromos: 5,
      archived: 0,
      perPageOptions: [5, 10, 25, 50],
      add_promo_code: false,
      edit_promo_code: false,
      view_only: false,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      date_format_cal: '',
      currencies: currencies.currencies,
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_symbol: '$',
      picker_time_format: 'hh:mm A',
      un_key: 0,
      bulk_update: false,
      modals: {
        promo_transaction: false,
        copy_to_groups: false,
      },
      errs: {
        name: '',
        value_amount: '',
        purchase_start_date: '',
        purchase_end_date: '',
        event_start_date: '',
        event_end_date: '',
        event_start_time: '',
        event_end_time: '',
        groups: '',
        bulk_promos: '',
        bulk_fields: ''
      },
      promo_transactions: [],
      groups: [],
      allGroups: [],
      selectedGroups: [],
      purchasable_items: [],
      games: [],
      selectedPromoCode: '',
      // Flat filter values for the shared <filter-bar>. Seeded from the URL in mounted;
      // FilterBar resolves saved-views precedence.
      filterValues: emptyPromoFilters(),
      confirmationDialog: {
        show: false,
        title: '',
        message: '',
        variant: 'warning',
        showCancel: false,
        confirmText: 'OK',
        confirmVariant: 'primary',
        onConfirm: null,
        onCancel: null
      },
      availablePromoCodeGroups: [],
      availableRuleTypes: [],
      promoRules: [],
      promo: {
        name: '',
        description: '',
        redemption_type: {name: 'Percentage discount', id: 0},
        value_amount: '1',
        quantity: '10',
        is_unlimited: true,
        value_type: {name: 'Per transaction', id: 0},
        games: [],
        groups: [],
        purchase_all_dates: {id: 0, name: 'Available any dates'},
        purchase_all_times: {id: 0, name: 'Available any times'},
        purchase_days: [],
        purchase_exlusions: [],
        purchase_start_date: new Date(),
        purchase_end_date: new Date(),
        event_all_dates: {id: 0, name: 'Available any dates'},
        event_all_times: {id: 0, name: 'Available any times'},
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        event_exlusions: [],
        event_start_date: new Date(),
        event_end_date: new Date(),
        event_start_time: new Date(),
        event_end_time: new Date(),
        is_combineable: false,
        is_cross_group: false
      },
      timeOptions: [
        {
          name: 'Available any times',
          id: 0
        },
        {
          name: 'Available between times',
          id: 1
        }
      ],
      dateOptions: [
        {
          name: 'Available any dates',
          id: 0
        },
        {
          name: 'Available between dates',
          id: 1
        }
      ],
      redemptionTypes: [
        {
          name: 'Percentage discount',
          id: 0
        },
        {
          name: 'Value discount',
          id: 1
        }
      ],
      valueTypes: [
        {
          name: 'Per transaction',
          id: 0
        },
        {
          name: 'Per line item',
          id: 1
        },
        {
          name: 'Per quantity',
          id: 2
        }
      ],
      all_promos: [],
      selectAllPromoCodesFlag: false,
      bulkSelectionMethod: 'individual',
      bulk_promo_codes: null,
      bulk_promo_code_groups: [],
      applyDefaultSettingsFromGroup: false,
      applyTemplateOnEdit: false,
      loadingTemplateSettings: false,
      originalPromoSettings: null,
      selected_bulk_update_fields: [],
      bulk_update_fields: [
        {
          id: 0,
          name: 'Description',
        },
        {
          id: 1,
          name: 'Redemption type',
        },
        {
          id: 2,
          name: 'Value type',
        },
        {
          id: 3,
          name: 'Amount',
        },
        {
          id: 4,
          name: 'Quantity',
        },
        {
          id: 5,
          name: 'Allowed purchase dates',
        },
        {
          id: 8,
          name: 'Can be combined with other promo codes',
        },
        {
          id: 9,
          name: 'Can only be applied internally',
        },
        {
          id: 21,
          name: 'Available at all locations',
        },
        {
          id: 10,
          name: 'Applicable to',
        },
        {
          id: 11,
          name: 'Allowed booking dates',
        },
        {
          id: 14,
          name: 'Allowed booking time',
        },
        {
          id: 17,
          name: 'Allowed days of the week',
        },
        {
          id: 18,
          name: 'Exclusion dates',
        },
        {
          id: 19,
          name: 'Promo code group',
        }
      ]
    }
  },
  components: {BaseAlert, Badge, TabRail,
    [TimePicker.name]: TimePicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Switch.name]: Switch,
    BTable, BPagination,
    PromoCodeTemplates,
    PromoRulesManager
  },
  computed: {
    // Options for the "Applicable to" select. Starts from the active purchasable items,
    // then merges in any currently linked item that has no active option (e.g. an archived
    // game/item/gift card type), so its chip renders a name instead of a blank tag.
    applicableToOptions() {
      const options = (this.purchasable_items || []).map(o => ({ ...o, displayLabel: o.name }))
      const selected = (this.promo && this.promo.purchasable_items) || []
      const optionUids = new Set(options.map(o => o.uid))
      const activeKeys = new Set(options.map(o => `${o.item_type}:${o.id}`))
      const extras = []
      const seenExtraUids = new Set()
      for (const sel of selected) {
        if (!sel || !sel.uid) continue
        if (optionUids.has(sel.uid) || seenExtraUids.has(sel.uid)) continue
        const isActive = activeKeys.has(`${sel.item_type}:${sel.id}`)
        extras.push({
          id: sel.id,
          name: sel.name,
          item_type: sel.item_type,
          uid: sel.uid,
          displayLabel: isActive ? sel.name : `${sel.name} (archived)`
        })
        seenExtraUids.add(sel.uid)
      }
      return options.concat(extras)
    },
    categories() {
      return [
        { name: 'Promo codes', shortName: 'Promos' },
        { name: 'Archived promo codes', shortName: 'Archived' },
        { name: 'Promo code groups', shortName: 'Groups' },
      ];
    },
    // Column keys equal the lambda sort keys so DataTable highlights the sorted header.
    promoColumns() {
      return [
        { key: 'name', label: 'Promo code', sortable: true },
        { key: 'group', label: 'Promo code group', sortable: true },
        { key: 'value', label: 'Value', sortable: true },
        { key: 'applicable_to', label: 'Applicable to' },
        { key: 'purchase_start', label: 'Purchase dates', sortable: true },
        { key: 'event_start', label: 'Event dates', sortable: true },
        { key: 'event_time', label: 'Event times', sortable: true },
        { key: 'remaining', label: 'Remaining', sortable: true },
        { key: 'used', label: 'Used', sortable: true }
      ]
    },
    // Saved views store the sort; `created` is the default newest first order.
    sortOptions() {
      return [
        { key: 'created', label: 'Date created' },
        { key: 'name', label: 'Promo code' },
        { key: 'group', label: 'Promo code group' },
        { key: 'value', label: 'Value' },
        { key: 'purchase_start', label: 'Purchase dates' },
        { key: 'event_start', label: 'Event dates' },
        { key: 'event_time', label: 'Event times' },
        { key: 'remaining', label: 'Remaining' },
        { key: 'used', label: 'Used' }
      ]
    },
    // Schema for the shared <filter-bar>: one filter per list column, plus campaign codes.
    // Every filter is primary; the bar folds chips into More filters only when the row is full.
    filterSchema() {
      return [
        { key: 'name', type: 'text', label: 'Promo code', placeholder: 'Promo code', operatorLabel: 'contains', primary: true },
        { key: 'template_name', type: 'multiselect', label: 'Promo code group', primary: true,
          options: (Array.isArray(this.availablePromoCodeGroups) ? this.availablePromoCodeGroups : []).map(g => ({ label: g.name, value: g.id })) },
        { key: 'generated', type: 'toggle', label: 'Issued by campaigns', primary: true },
        { key: 'value_kind', type: 'multiselect', label: 'Discount type', primary: true, options: this.redemptionTypes.map(t => ({ label: t.name, value: t.id })) },
        { key: 'value_amount', type: 'numberrange', label: 'Value', primary: true },
        { key: 'value_type', type: 'multiselect', label: 'Applies per', primary: true, options: this.valueTypes.map(t => ({ label: t.name, value: t.id })) },
        { key: 'applies_to', type: 'multiselect', label: 'Applicable to', primary: true, options: this.appliesToOptions },
        { key: 'purchase_dates', type: 'daterange', label: 'Purchase dates', primary: true },
        { key: 'event_dates', type: 'daterange', label: 'Event dates', primary: true },
        { key: 'event_times', type: 'timerange', label: 'Event times', primary: true },
        { key: 'remaining', type: 'numberrange', label: 'Remaining', allowUnlimited: true, primary: true },
        { key: 'used', type: 'numberrange', label: 'Used', primary: true }
      ]
    },
    // Applicable to filter options: all locations, then the group's events, merchandise
    // and gift cards. Values match the lambda's applies_to tokens (game:<id> and so on).
    appliesToOptions() {
      const groups = [['game', 'Events'], ['item', 'Merchandise'], ['gift_card_type', 'Gift cards']]
      const items = Array.isArray(this.purchasable_items) ? this.purchasable_items : []
      const options = [{ label: 'All locations', value: 'all_locations' }]
      for (const [type, group] of groups) {
        for (const item of items.filter(i => i.item_type === type)) {
          options.push({ label: item.name, value: type + ':' + item.id, group })
        }
      }
      return options
    },
    selectedGroupHasDefaultSettings() {
      if (!this.promo.template_id) return false
      const selectedGroup = this.availablePromoCodeGroups.find(g => g.id === this.promo.template_id)
      return selectedGroup && selectedGroup.has_template === 1
    },
    selectedGroupForBulkUpdate() {
      if (!this.promo.template_id) return null
      return this.availablePromoCodeGroups.find(g => g.id === this.promo.template_id)
    },
    hasBuyXGetYRules() {
      if (!this.promoRules || this.promoRules.length === 0) return false
      return this.promoRules.some(rule => {
        const ruleType = this.availableRuleTypes.find(rt => rt.id === rule.rule_type_id)
        return ruleType && ruleType.rule_key === 'BUY_X_GET_Y'
      })
    }
  },
  methods: {
    handleCreateTemplate() {
      if (this.$refs.promoCodeTemplates) {
        this.$refs.promoCodeTemplates.createNewTemplate()
      }
    },

    handleEditTemplate(template) {
      // Edit template logic
    },

    handleCreateFromTemplate(template) {
      // Populate promo form with template data
      this.switchTab(0)
      this.addNewPromo()
      // Populate this.promo with template data
      Object.assign(this.promo, template)
    },
    // Promo Rules Methods
    getItemsByType(itemType) {
      if (!this.purchasable_items) return [];
      return this.purchasable_items.filter(item => item.item_type === itemType);
    },
    handleAddRule(rule) {
      this.promoRules.push(rule);
    },
    handleUpdateRule(index, rule) {
      this.$set(this.promoRules, index, rule);
    },
    handleRemoveRule(index) {
      this.promoRules.splice(index, 1);
    },
    formatCurrencyValue(amount) {
      const formattedAmount = parseFloat(amount).toFixed(2);
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      if (isPostfix) {
        return formattedAmount + this.currency_symbol;  // 12.34kr
      } else {
        return this.currency_symbol + formattedAmount;  // $12.34
      }
    },
    addAllFields(){
      this.selected_bulk_update_fields = this.bulk_update_fields
    },
    cancelEdit(){
      this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived)
      this.edit_promo_code = false
      this.view_only = false
    },
    bulkUpdateFieldSelected(fieldName){
      let found = _.find(this.selected_bulk_update_fields, {name: fieldName})
      return found
    },
    getPromoLinks(linkString){
      let arr = JSON.parse(linkString)
      const activeKeys = new Set((this.purchasable_items || []).map(o => `${o.item_type}:${o.id}`))
      return arr.map(item => {
        const isArchived = activeKeys.size > 0 && !activeKeys.has(`${item.item_type}:${item.id}`)
        return isArchived ? `${item.name} (archived)` : item.name
      }).join(', ');
    },
    async bulkUpdatePromoCodes(){

      // Validation - skip field validation if applying default settings from group
      if(!this.applyDefaultSettingsFromGroup && this.selected_bulk_update_fields.length <= 0){
        this.errs.bulk_fields = 'Please select one or more fields to update'
        return
      } else {
        this.errs.bulk_fields = ''
      }

      // Validate that a group is selected when applying default settings
      if(this.applyDefaultSettingsFromGroup && !this.promo.template_id){
        this.errs.bulk_fields = 'Please select a promo code group to apply default settings from'
        return
      }

      if(!this.selectAllPromoCodesFlag){
        if(this.bulkSelectionMethod === 'group' && (!this.bulk_promo_code_groups || this.bulk_promo_code_groups.length <= 0)){
          this.errs.bulk_promos = 'Please select one or more promo code groups'
          return
        } else if(this.bulkSelectionMethod === 'individual' && (!this.bulk_promo_codes || this.bulk_promo_codes.length <= 0)){
          this.errs.bulk_promos = 'Please select one or more promo codes'
          return
        } else {
          this.errs.bulk_promos = ''
        }
      } else {
        this.errs.bulk_promos = ''
      }

      const affectedCount = this.selectAllPromoCodesFlag
        ? this.all_promos.length
        : this.bulk_promo_codes.length
      const changeSummary = this.applyDefaultSettingsFromGroup
        ? `the default settings from ${this.selectedGroupForBulkUpdate ? this.selectedGroupForBulkUpdate.name : 'the selected group'}`
        : `${this.selected_bulk_update_fields.length} field(s): ${this.selected_bulk_update_fields.map(f => f.name).join(', ')}`
      const confirmed = await this.confirmAction({
        title: 'Apply bulk update?',
        message: `This will overwrite ${changeSummary} on ${affectedCount} promo code(s). This cannot be undone.`,
        confirmText: 'Update promo codes'
      })
      if(!confirmed) return

      this.loading = true
      const token = await this.getAuthToken()
      const config = {headers: {Authorization: token}}
      const companyId = localStorage.getItem('companyId')
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos'

      let fieldsToSend = []
      // Same transform as the single-code save path: the Quantity toggle stores
      // is_unlimited separately, but the backend only reads quantity (-1 = unlimited)
      let settingsToSend = { ...this.promo, quantity: this.promo.is_unlimited == true ? '-1' : this.promo.quantity }

      // If applying default settings from group, use template settings
      if (this.applyDefaultSettingsFromGroup && this.selectedGroupForBulkUpdate) {
        const template = this.selectedGroupForBulkUpdate

        // Build fields array for all template fields (excluding description)
        fieldsToSend = [
          { id: 19, name: 'Promo code group' },
          { id: 1, name: 'Redemption type' },
          { id: 2, name: 'Value type' },
          { id: 3, name: 'Amount' },
          { id: 5, name: 'Allowed purchase dates' },
          { id: 8, name: 'Can be combined with other promo codes' },
          { id: 9, name: 'Can only be applied internally' },
          { id: 10, name: 'Applicable to' },
          { id: 11, name: 'Allowed booking dates' },
          { id: 14, name: 'Allowed booking time' },
          { id: 17, name: 'Allowed days of the week' },
          { id: 18, name: 'Exclusion dates' }
        ]

        // Add date/time range fields if applicable
        if (template.purchase_is_range) {
          fieldsToSend.push({ id: 6, name: 'Purchase start date' })
          fieldsToSend.push({ id: 7, name: 'Purchase end date' })
        }
        if (template.event_is_date_range) {
          fieldsToSend.push({ id: 12, name: 'Event start date' })
          fieldsToSend.push({ id: 13, name: 'Event end date' })
        }
        if (template.event_is_time_range) {
          fieldsToSend.push({ id: 15, name: 'Event start time' })
          fieldsToSend.push({ id: 16, name: 'Event end time' })
        }

        // Map template settings to the format expected by bulk update
        settingsToSend = {
          ...settingsToSend,
          template_id: template.id,
          redemption_type: { id: template.redemption_type },
          value_type: { id: template.value_type },
          value_amount: template.value_amount,
          purchase_all_dates: { id: template.purchase_is_range ? 1 : 0 },
          purchase_start_date: template.purchase_start_date,
          purchase_end_date: template.purchase_end_date,
          event_all_dates: { id: template.event_is_date_range ? 1 : 0 },
          event_start_date: template.event_start_date,
          event_end_date: template.event_end_date,
          event_all_times: { id: template.event_is_time_range ? 1 : 0 },
          event_start_time: template.event_start_time,
          event_end_time: template.event_end_time,
          available_monday: template.available_monday,
          available_tuesday: template.available_tuesday,
          available_wednesday: template.available_wednesday,
          available_thursday: template.available_thursday,
          available_friday: template.available_friday,
          available_saturday: template.available_saturday,
          available_sunday: template.available_sunday,
          is_combineable: template.is_combineable,
          internal_only: template.internal_only,
          purchasable_items: template.purchasable_items || [],
          event_exlusions: template.event_exclusions || []
        }
      } else {
        // Build fields array with auto-injected child fields
        fieldsToSend = [...this.selected_bulk_update_fields]

        // If "Allowed purchase dates" is selected and "Between dates" chosen, add child fields
        if (this.bulkUpdateFieldSelected('Allowed purchase dates') && this.promo.purchase_all_dates.id == 1) {
          fieldsToSend.push({ id: 6, name: 'Purchase start date' })
          fieldsToSend.push({ id: 7, name: 'Purchase end date' })
        }

        // If "Allowed booking dates" is selected and "Between dates" chosen, add child fields
        if (this.bulkUpdateFieldSelected('Allowed booking dates') && this.promo.event_all_dates.id == 1) {
          fieldsToSend.push({ id: 12, name: 'Event start date' })
          fieldsToSend.push({ id: 13, name: 'Event end date' })
        }

        // If "Allowed booking time" is selected and "Between times" chosen, add child fields
        if (this.bulkUpdateFieldSelected('Allowed booking time') && this.promo.event_all_times.id == 1) {
          fieldsToSend.push({ id: 15, name: 'Event start time' })
          fieldsToSend.push({ id: 16, name: 'Event end time' })
        }
      }

      try {
        if (this.selectAllPromoCodesFlag) {
          const chunkSize = 100; // Update 100 at a time
          const chunks = [];

          for (let i = 0; i < this.all_promos.length; i += chunkSize) {
            chunks.push(this.all_promos.slice(i, i + chunkSize));
          }

          for (const chunk of chunks) {
            const data = {
              settings: settingsToSend,
              promo_codes: chunk.map(p => p.id), // Send only IDs
              fields: fieldsToSend
            };

            await this.axios.put(url, data, config);
          }

          const successMsg = this.applyDefaultSettingsFromGroup
            ? 'All promo codes have been updated with default settings from group'
            : 'All promo codes have been updated';
          this.showAlert('Success', successMsg, 'success')

        } else {
          const data = {
            settings: settingsToSend,
            promo_codes: this.bulk_promo_codes.map(p => p.id),
            fields: fieldsToSend
          };

          await this.axios.put(url, data, config);
          const successMsg = this.applyDefaultSettingsFromGroup
            ? 'Selected promo codes have been updated with default settings from group'
            : 'Selected promo codes have been updated';
          this.showAlert('Success', successMsg, 'success')
        }
      } catch (err) {
        console.error('Bulk update error:', err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/')
        } else {
          this.showAlert('Error', 'Failed to update promo codes. Please try again.', 'danger')
        }
        this.loading = false
        return
      }

      this.loading = false

      // Reset toggle state
      this.applyDefaultSettingsFromGroup = false

      // Close bulk update view
      this.bulk_update = false

      // Switch to first tab
      this.switchTab(0)

      // Re-fetch promo codes
      await this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived)

    },
    handleBulkGroupSelection() {
      // Initialize bulk_promo_codes if null
      if (!this.bulk_promo_codes) {
        this.bulk_promo_codes = []
      }

      // Get the IDs of selected groups
      const selectedGroupIds = this.bulk_promo_code_groups.map(g => g.id)

      if (selectedGroupIds.length === 0) {
        // If no groups are selected, remove all promo codes that were from groups
        // Keep only manually selected promo codes (those without template_id or not matching any previous group)
        this.bulk_promo_codes = this.bulk_promo_codes.filter(selectedPromo => {
          const fullPromo = this.all_promos.find(p => p.id === selectedPromo.id)
          return !fullPromo || !fullPromo.template_id
        })
        return
      }

      // Remove promo codes that are no longer in selected groups
      this.bulk_promo_codes = this.bulk_promo_codes.filter(selectedPromo => {
        const fullPromo = this.all_promos.find(p => p.id === selectedPromo.id)
        // Keep if: no template_id (manually added) OR template_id is in selected groups
        return !fullPromo || !fullPromo.template_id || selectedGroupIds.includes(fullPromo.template_id)
      })

      // Find all promo codes that belong to the selected groups
      const promosInSelectedGroups = this.all_promos.filter(promo =>
        promo.template_id && selectedGroupIds.includes(promo.template_id)
      )

      // Add promo codes from selected groups (avoid duplicates)
      promosInSelectedGroups.forEach(promo => {
        const exists = this.bulk_promo_codes.find(p => p.id === promo.id)
        if (!exists) {
          this.bulk_promo_codes.push({id: promo.id, name: promo.name})
        }
      })
    },
    async bulkUpdatePrompt(){
      this.bulkSelectionMethod = 'individual'
      this.bulk_promo_codes = null
      this.bulk_promo_code_groups = []
      this.selected_bulk_update_fields = null
      this.applyDefaultSettingsFromGroup = false
      this.promo = {
        name: '',
        description: '',
        template_id: null,
        redemption_type: {name: 'Percentage discount', id: 0},
        value_amount: '1',
        quantity: '10',
        is_unlimited: true,
        value_type: {name: 'Per transaction', id: 0},
        games: [],
        groups: [],
        purchase_all_dates: {id: 0, name: 'Available any dates'},
        purchase_all_times: {id: 0, name: 'Available any times'},
        purchase_days: [],
        purchase_exlusions: [],
        purchase_start_date: new Date(),
        purchase_end_date: new Date(),
        event_all_dates: {id: 0, name: 'Available any dates'},
        event_all_times: {id: 0, name: 'Available any times'},
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        event_exlusions: [],
        event_start_date: new Date(),
        event_end_date: new Date(),
        event_start_time: new Date(),
        event_end_time: new Date(),
        is_combineable: false,
        is_cross_group: false
      }
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      this.loading = true

      // Use minimal endpoint for fast loading - only fetches id, name, template_id
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?minimal=1&archived=' + this.archived

      try {
        const response = await this.axios.get(url, config)
        this.all_promos = _.sortBy(response.data.promos, 'name')
      } catch (err) {
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/')
        } else {
          this.showAlert('Error', 'Something went wrong', 'danger')
        }
        this.loading = false
        return
      }

      await this.getAvailablePromoCodeGroups()
      this.loading = false
      this.bulk_update = true
    },
    addAllPurchaseTypes(collection, type) {
      const existingIds = new Set(this.promo.purchasable_items.map(item => item.id));
      const newItems = collection.filter(item => item.item_type === type && !existingIds.has(item.id));
      this.promo.purchasable_items = this.promo.purchasable_items.concat(newItems);
      this.un_key++
    },
    appliesToGames(){
      if(!this.promo.purchasable_items){
        return false
      }
      return this.promo.purchasable_items.some(item => item.item_type === 'game');
    },
    handleCrossGroupToggle(newValue) {
      if (newValue && this.promo.purchasable_items && this.promo.purchasable_items.length > 0) {
        // Clear the applicable items when enabling cross-group
        this.promo.purchasable_items = []
        this.un_key++
      }
    },
    isPromoFromCurrentGroup(item) {
      const currentGroupId = JSON.parse(localStorage.getItem('group')).id
      return item.company_group_id === currentGroupId
    },
    promoGroupHasDefaults(item) {
      if (!item || !item.template_id) return false
      const group = this.availablePromoCodeGroups.find(g => g.id === item.template_id)
      return !!(group && group.has_template === 1)
    },
    // Mirrors PromoCodeTemplates.vue mapTemplateToSettings/getTemplateFields so a single-code
    // apply produces identical results to the per-group "apply to all" flow.
    mapGroupToSettings(group) {
      return {
        redemption_type: { id: group.redemption_type },
        value_type: { id: group.value_type },
        value_amount: group.value_amount,
        purchase_all_dates: { id: group.purchase_is_range ? 1 : 0 },
        purchase_start_date: group.purchase_start_date,
        purchase_end_date: group.purchase_end_date,
        event_all_dates: { id: group.event_is_date_range ? 1 : 0 },
        event_start_date: group.event_start_date,
        event_end_date: group.event_end_date,
        event_all_times: { id: group.event_is_time_range ? 1 : 0 },
        event_start_time: group.event_start_time,
        event_end_time: group.event_end_time,
        available_monday: group.available_monday,
        available_tuesday: group.available_tuesday,
        available_wednesday: group.available_wednesday,
        available_thursday: group.available_thursday,
        available_friday: group.available_friday,
        available_saturday: group.available_saturday,
        available_sunday: group.available_sunday,
        is_combineable: group.is_combineable,
        internal_only: group.internal_only,
        purchasable_items: group.purchasable_items || [],
        event_exlusions: group.event_exclusions || []
      }
    },
    getGroupFields(group) {
      // Description is intentionally excluded - the group description is for the group, not the code
      let fields = [
        { id: 1, name: 'Redemption type' },
        { id: 2, name: 'Value type' },
        { id: 3, name: 'Amount' },
        { id: 5, name: 'Allowed purchase dates' },
        { id: 8, name: 'Can be combined with other promo codes' },
        { id: 9, name: 'Can only be applied internally' },
        { id: 10, name: 'Applicable to' },
        { id: 11, name: 'Allowed booking dates' },
        { id: 14, name: 'Allowed booking time' },
        { id: 17, name: 'Allowed days of the week' },
        { id: 18, name: 'Exclusion dates' }
      ]
      if (group.purchase_is_range) {
        fields.push({ id: 6, name: 'Purchase start date' })
        fields.push({ id: 7, name: 'Purchase end date' })
      }
      if (group.event_is_date_range) {
        fields.push({ id: 12, name: 'Event start date' })
        fields.push({ id: 13, name: 'Event end date' })
      }
      if (group.event_is_time_range) {
        fields.push({ id: 15, name: 'Event start time' })
        fields.push({ id: 16, name: 'Event end time' })
      }
      return fields
    },
    async applyGroupSettingsToPromo(promo, event) {
      if (event) event.preventDefault()

      const group = this.availablePromoCodeGroups.find(g => g.id === promo.template_id)
      if (!group || group.has_template !== 1) {
        this.showAlert('No default settings', "This promo code's group doesn't have default settings to apply.", 'info')
        return
      }

      const confirmed = await this.confirmAction({
        title: 'Apply group settings?',
        message: `This will overwrite the settings on ${promo.name} with the default settings from ${group.name}. ` +
          'Existing settings (allowed days, dates, times, exclusions, applicable items, etc.) will be replaced.',
        confirmText: 'Apply settings'
      })

      if (!confirmed) return

      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos'

        const games = (group.purchasable_items || [])
          .filter(item => item.item_type === 'game')
          .map(item => ({ id: item.id }))

        const data = {
          promo_codes: [promo.id],
          settings: this.mapGroupToSettings(group),
          fields: this.getGroupFields(group),
          games: games,
          purchasable_items: group.purchasable_items || []
        }

        await this.axios.put(url, data, config)
        this.showAlert('Success', `Applied ${group.name} default settings to ${promo.name}`, 'success')
        await this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived)
      } catch (err) {
        console.error('Apply group settings error:', err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/')
        } else {
          this.showAlert('Error', 'Failed to apply group settings. Please try again.', 'danger')
        }
      } finally {
        this.loading = false
      }
    },
    getOriginGroupName() {
      if (!this.promo || !this.promo.company_group_id) return ''
      const group = this.allGroups.find(g => g.id === this.promo.company_group_id)
      return group ? group.name : ''
    },
    async viewPromoCode(item, event) {
      event.preventDefault()
      this.view_only = true
      this.promo = item
      this.promo.redemption_type = {id: item.redemption_type}
      this.promo.value_type = {id: item.value_type}
      this.promo.purchase_all_dates = {id: item.purchase_is_range}
      this.promo.event_all_dates = {id: item.event_is_date_range}
      this.promo.event_all_times = {id: item.event_is_time_range}
      this.promo.internal_only = Boolean(this.promo.internal_only)
      this.promo.is_combineable = Boolean(this.promo.is_combineable)
      this.$set(this.promo, 'is_cross_group', Boolean(item.is_cross_group))
      this.promo.purchasable_items = JSON.parse(item.linked_games).concat(JSON.parse(item.linked_items)).concat(JSON.parse(item.linked_gift_card_types))

      let dt = new Date()
      if(!(this.promo.event_start_time instanceof Date)){
        dt.setHours(this.promo.event_start_time.split(':')[0])
        dt.setMinutes(this.promo.event_start_time.split(':')[1])
        this.promo.event_start_time = dt
      }
      if(!(this.promo.event_end_time instanceof Date)){
        dt = new Date()
        dt.setHours(this.promo.event_end_time.split(':')[0])
        dt.setMinutes(this.promo.event_end_time.split(':')[1])
        this.promo.event_end_time = dt
      }
      if(item.event_exlusions && !Array.isArray(item.event_exlusions)){
        let exl = []
        let obj = JSON.parse(item.event_exlusions)
        for(let n in obj){
          exl.push(obj[n].exclusion_date)
        }
        item.event_exlusions = exl
      }

      if(this.promo.quantity.toString() === '-1'){
        this.promo.is_unlimited = true
        this.un_key++
      } else {
        this.promo.is_unlimited = false
        this.un_key++
      }
      await this.getGroups()
      await this.getAvailablePromoCodeGroups()

      this.promoRules = item.rules || []
      this.edit_promo_code = true
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    async copyPromoCode(){
      if(this.selectedGroups.length <= 0){
        this.errs.groups = 'Please select groups'
        return
      } else {
        this.errs.groups = ''
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos/' + this.selectedPromoCode.id
      let data = {
        groups: this.selectedGroups
      }

      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code has been copied', 'success')
          this.getPromos(this.perPage, this.getOffset(this.currentPage,  this.perPage), this.archived)
          this.modals.copy_to_groups = false
          this.selectedGroups = []
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getGroups(){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups'

      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.allGroups = response.data
          this.groups = response.data.filter(g => g.id !== JSON.parse(localStorage.getItem('group')).id)
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', () => this.$router.push('/'))
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async copyToGroupsPrompt(item, event){
      event.preventDefault()
      this.selectedPromoCode = item
      this.selectedGroups = []
      await this.getGroups()
      this.modals.copy_to_groups = true
    },
    goToTransaction(id){
      let documentClasses = document.body.classList;
      documentClasses.remove('modal-open');
      this.$router.push({ path: '/purchases/transactions/details', query: { id: id }})
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    validateDateRange(){
      let errors = 0
      if(this.promo.purchase_all_dates.id === 1){
        if(this.promo.purchase_start_date instanceof Date){
          this.promo.purchase_start_date = this.promo.purchase_start_date.toISOString()
        }
        if(this.promo.purchase_end_date instanceof Date){
          this.promo.purchase_end_date = this.promo.purchase_end_date.toISOString()
        }
        if(this.promo.purchase_start_date.includes('T')){
          this.promo.purchase_start_date = this.promo.purchase_start_date.split('T')[0]
        }
        if(this.promo.purchase_end_date.includes('T')){
          this.promo.purchase_end_date = this.promo.purchase_end_date.split('T')[0]
        }
        let start = new Date()
        start.setFullYear(this.promo.purchase_start_date.split("-")[0])
        start.setMonth(parseInt(this.promo.purchase_start_date.split("-")[1]) - 1, this.promo.purchase_start_date.split("-")[2])

        let end = new Date()
        end.setFullYear(this.promo.purchase_end_date.split("-")[0])
        end.setMonth(parseInt(this.promo.purchase_end_date.split("-")[1]) - 1, this.promo.purchase_end_date.split("-")[2])
        if(start > end){
          this.errs.purchase_start_date = 'Start date cannot be greater than end date'
          errors ++
        } else {
          this.errs.purchase_start_date = ''
        }
      }

      if(this.promo.event_all_dates.id === 1){
        if(this.promo.event_start_date instanceof Date){
          this.promo.event_start_date = this.promo.event_start_date.toISOString()
        }
        if(this.promo.event_end_date instanceof Date){
          this.promo.event_end_date = this.promo.event_end_date.toISOString()
        }

        if(this.promo.event_start_date.includes('T')){
          this.promo.event_start_date = this.promo.event_start_date.split('T')[0]
        }
        if(this.promo.event_end_date.includes('T')){
          this.promo.event_end_date = this.promo.event_end_date.split('T')[0]
        }
        let event_start = new Date()
        event_start.setFullYear(this.promo.event_start_date.split("-")[0])
        event_start.setMonth(parseInt(this.promo.event_start_date.split("-")[1]) - 1, this.promo.event_start_date.split("-")[2])

        let event_end = new Date()
        event_end.setFullYear(this.promo.event_end_date.split("-")[0])
        event_end.setMonth(parseInt(this.promo.event_end_date.split("-")[1]) - 1, this.promo.event_end_date.split("-")[2])

        if(event_start > event_end){
          this.errs.event_start_date = 'Start date cannot be greater than end date'
          errors ++
        } else {
          this.errs.event_start_date = ''
        }
      }

      if(this.promo.event_all_times.id === 1){

        // Compare time of day only: typed picker input parses to Jan 1 while
        // panel-picked values keep today's date, so full Dates aren't comparable
        const startMinutes = this.promo.event_start_time.getHours() * 60 + this.promo.event_start_time.getMinutes()
        const endMinutes = this.promo.event_end_time.getHours() * 60 + this.promo.event_end_time.getMinutes()
        if(startMinutes > endMinutes){
          this.errs.event_start_time = 'Start time cannot be greater than end time'
          errors ++
        } else {
          this.errs.event_start_time = ''
        }
      }

      return errors
    },
    // FilterBar change: apply new values and (re)fetch. The first change is FilterBar's
    // initial saved-views resolution and drives the first load (preserving the URL page).
    onFiltersChanged(values){
      this.filterValues = {
        ...emptyPromoFilters(),
        ...values,
        name: values.name || '',
        template_name: Array.isArray(values.template_name) ? values.template_name : [],
        generated: values.generated === true
      }
      this.dropDeletedGroupsFromFilter()
      if(!this.didInitialFetch){
        this.didInitialFetch = true
        this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived)
      } else {
        // Applying a filter/view lands on page 1; reset before computing the offset
        // (getOffset uses currentPage, so a stale page would build a bad offset).
        this.currentPage = 1
        this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived, 'filter')
      }
    },
    // Per page select, or a restored saved view (meta.restoring: the change that follows fetches).
    onPerPageChange(n, meta){
      this.perPage = n
      if(meta && meta.restoring){
        if(this.didInitialFetch) this.currentPage = 1
        return
      }
      this.currentPage = 1
      this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived, 'filter')
    },
    // Header click or a restored saved view. A restore (meta.restoring) is followed by
    // FilterBar's change, which fetches; on first load that change keeps the URL page.
    onSortChange(value, meta){
      this.sort = { key: value.key, dir: value.dir === 'asc' ? 'asc' : 'desc' }
      if(meta && meta.restoring) return
      this.currentPage = 1
      if(this.didInitialFetch){
        this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived, 'filter')
      }
    },
    // Filter and sort params for the list, shared by the page fetch and the CSV export.
    // Param names match buildPromoFilterWhere in get-promos-for-group.
    promoFilterQuery(){
      const f = { ...emptyPromoFilters(), ...this.filterValues }
      const has = v => v !== '' && v != null
      const parts = [
        'name=' + encodeURIComponent(f.name || ''),
        'template_ids=' + encodeURIComponent((f.template_name || []).join(',')),
        'generated=' + (f.generated === true ? '1' : '0')
      ]
      if((f.value_kind || []).length > 0) parts.push('value_kinds=' + f.value_kind.join(','))
      if((f.value_type || []).length > 0) parts.push('value_types=' + f.value_type.join(','))
      if(f.value_amount && has(f.value_amount.from) && has(f.value_amount.to)){
        parts.push('value_from=' + encodeURIComponent(f.value_amount.from), 'value_to=' + encodeURIComponent(f.value_amount.to))
      }
      if((f.applies_to || []).length > 0) parts.push('applies_to=' + encodeURIComponent(f.applies_to.join(',')))
      const purchase = this.filterDateBounds(f.purchase_dates)
      if(purchase) parts.push('purchase_from=' + purchase.from, 'purchase_to=' + purchase.to)
      const eventDates = this.filterDateBounds(f.event_dates)
      if(eventDates) parts.push('event_from=' + eventDates.from, 'event_to=' + eventDates.to)
      if(f.event_times && f.event_times.from && f.event_times.to){
        parts.push('time_from=' + encodeURIComponent(f.event_times.from), 'time_to=' + encodeURIComponent(f.event_times.to))
      }
      if(f.remaining && f.remaining.unlimited){
        parts.push('remaining_unlimited=1')
      } else if(f.remaining && has(f.remaining.from) && has(f.remaining.to)){
        parts.push('remaining_from=' + encodeURIComponent(f.remaining.from), 'remaining_to=' + encodeURIComponent(f.remaining.to))
      }
      if(f.used && has(f.used.from) && has(f.used.to)){
        parts.push('used_from=' + encodeURIComponent(f.used.from), 'used_to=' + encodeURIComponent(f.used.to))
      }
      parts.push('sort=' + encodeURIComponent(this.sort.key), 'dir=' + this.sort.dir)
      return parts.join('&')
    },
    // A date range filter as YYYY-MM-DD bounds; "within the last N days" ends today.
    filterDateBounds(v){
      if(!v) return null
      if(v.mode === 'days' && v.days){
        const ymd = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        const from = new Date()
        from.setDate(from.getDate() - Number(v.days))
        return { from: ymd(from), to: ymd(new Date()) }
      }
      if(v.mode === 'range' && v.from && v.to) return { from: v.from, to: v.to }
      return null
    },
    onPageChange(page){
      this.currentPage = page
      this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived)
    },
    async getAvailablePromoCodeGroups() {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates?count=1000&offset=0'

      axios.get(url, config)
        .then((res) => {
          this.availablePromoCodeGroups = res.data.templates || []
          // The request asks for up to 1000 groups; a full page may be missing some,
          // so the filter is only checked against a complete list.
          this.promoGroupsLoaded = this.availablePromoCodeGroups.length < 1000
          if(this.dropDeletedGroupsFromFilter() && this.didInitialFetch){
            this.currentPage = 1
            this.getPromos(this.perPage, this.getOffset(this.currentPage, this.perPage), this.archived, 'filter')
          }
        })
        .catch((err) => {
          console.error('Failed to fetch promo code groups', err)
        })
    },
    // A deleted group can linger in the group filter (URL, a saved view, or the
    // last-used filters). Removes any group id that is no longer a live group and
    // syncs the filter bar. Returns true when something was removed.
    dropDeletedGroupsFromFilter() {
      if(!this.promoGroupsLoaded) return false
      const liveIds = new Set(this.availablePromoCodeGroups.map(g => Number(g.id)))
      const selected = this.filterValues.template_name
      const kept = selected.filter(id => liveIds.has(Number(id)))
      if(kept.length === selected.length) return false
      this.filterValues = { ...this.filterValues, template_name: kept }
      if(this.$refs.filterBar){
        this.$refs.filterBar.applyValues(this.filterValues)
      }
      return true
    },
    async handleTemplateSelection(templateId) {
      // Reset apply template toggle when group selection changes
      this.applyTemplateOnEdit = false

      // If cleared or no template selected, reset bulk update toggle too
      if (!templateId) {
        this.applyDefaultSettingsFromGroup = false
        return
      }

      // Check if the newly selected group has default settings
      const selectedGroup = this.availablePromoCodeGroups.find(g => g.id === templateId)
      if (!selectedGroup || selectedGroup.has_template !== 1) {
        // Reset bulk update toggle if group doesn't have default settings
        this.applyDefaultSettingsFromGroup = false
      }

      // Don't auto-apply settings - let user decide via toggle
      // The toggle will appear if the selected group has default settings
    },
    async applyTemplateSettingsToPromo(templateId) {
      // Fetch full template details and apply to current promo
      try {
        this.loadingTemplateSettings = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates/' + templateId

        const response = await axios.get(url, config)
        const template = response.data.template

        // Preserve the promo code name and template_id
        const promoName = this.promo.name
        const promoTemplateId = this.promo.template_id

        // Populate form fields from template
        this.promo = {
          ...this.promo,
          name: promoName, // Keep the original name
          template_id: promoTemplateId, // Keep the template_id
          description: template.description || '',
          redemption_type: {name: template.redemption_type === 0 ? 'Percentage discount' : 'Value discount', id: template.redemption_type},
          value_amount: template.value_amount,
          value_type: {name: template.value_type === 0 ? 'Per transaction' : 'Per item', id: template.value_type},
          purchase_all_dates: {id: template.purchase_is_range, name: template.purchase_is_range === 1 ? 'Available within date range' : 'Available any dates'},
          purchase_start_date: template.purchase_start_date ? new Date(template.purchase_start_date) : new Date(),
          purchase_end_date: template.purchase_end_date ? new Date(template.purchase_end_date) : new Date(),
          event_all_dates: {id: template.event_is_date_range, name: template.event_is_date_range === 1 ? 'Available within date range' : 'Available any dates'},
          event_all_times: {id: template.event_is_time_range, name: template.event_is_time_range === 1 ? 'Available within time range' : 'Available any times'},
          event_start_date: template.event_start_date ? new Date(template.event_start_date) : new Date(),
          event_end_date: template.event_end_date ? new Date(template.event_end_date) : new Date(),
          is_combineable: Boolean(template.is_combineable),
          internal_only: Boolean(template.internal_only),
          available_monday: Boolean(template.available_monday),
          available_tuesday: Boolean(template.available_tuesday),
          available_wednesday: Boolean(template.available_wednesday),
          available_thursday: Boolean(template.available_thursday),
          available_friday: Boolean(template.available_friday),
          available_saturday: Boolean(template.available_saturday),
          available_sunday: Boolean(template.available_sunday),
        }

        // Handle event times
        if (template.event_start_time) {
          let dt = new Date()
          dt.setHours(template.event_start_time.split(':')[0])
          dt.setMinutes(template.event_start_time.split(':')[1])
          this.promo.event_start_time = dt
        }
        if (template.event_end_time) {
          let dt = new Date()
          dt.setHours(template.event_end_time.split(':')[0])
          dt.setMinutes(template.event_end_time.split(':')[1])
          this.promo.event_end_time = dt
        }

        // Handle purchasable items
        if (template.linked_games || template.linked_items || template.linked_gift_card_types) {
          const games = template.linked_games ? JSON.parse(template.linked_games) : []
          const items = template.linked_items ? JSON.parse(template.linked_items) : []
          const giftCards = template.linked_gift_card_types ? JSON.parse(template.linked_gift_card_types) : []
          this.promo.purchasable_items = [...games, ...items, ...giftCards]
        }

        // Handle event exclusions
        if (template.event_exclusions) {
          const exclusions = JSON.parse(template.event_exclusions)
          this.promo.event_exlusions = exclusions.map(e => e.exclusion_date)
        }

        this.loadingTemplateSettings = false
      } catch (err) {
        this.loadingTemplateSettings = false
        console.error('Failed to fetch template details', err)
        this.$notify({
          type: 'danger',
          message: 'Failed to load template settings',
          timeout: 3000
        })
      }
    },
    async handleApplyTemplateOnEditToggle(value) {
      if (value && this.promo.template_id) {
        // When toggle is enabled, apply the template settings
        await this.applyTemplateSettingsToPromo(this.promo.template_id)
      } else if (!value && this.edit_promo_code && this.originalPromoSettings) {
        // When toggle is disabled during edit, revert to original settings (but keep the new template_id)
        const currentTemplateId = this.promo.template_id
        this.promo = JSON.parse(JSON.stringify(this.originalPromoSettings))
        this.promo.template_id = currentTemplateId
      }
      // For add_promo_code, when toggle is disabled, just keep current form values (no revert needed)
    },
    handleApplyDefaultSettingsToggle(value) {
      if (value) {
        // When toggle is ON, keep only "Promo code group" field selected
        this.selected_bulk_update_fields = [{ id: 19, name: 'Promo code group' }]
      }
    },
    async getTransactions(item, event){
      event.preventDefault()
      this.selectedPromoCode = item
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos/' + item.id

      axios.get(url, config)
        .then((response) => {
          this.modals.promo_transaction = true
          this.promo_transactions = response.data.transactions
          this.totalRowsPromos = this.promo_transactions.length
          this.loading = false

        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })

    },
    getDate(d){
      return formattedDate(this.date_format, d)
    },
    getTime(d){
      return time(this.time_format, d)
    },
    isPromoValid(){
      let errors = 0

      //check that name is unique
      let name = this.promo.name
      let id = this.promo.id
      let non_unique = _.find(this.promo_codes, function(o) { return o.name === name && o.id !== id })
      if(non_unique){
        this.errs.name = 'Promo code name must be unique'
        errors++
      } else if(this.promo.name.length <= 0){
        this.errs.name = 'Promo code name cannot be empty'
        errors++
      } else {
        this.errs.name = ''
      }

      if(isNaN(this.promo.value_amount) || parseFloat(this.promo.value_amount) < 0){
        this.errs.value_amount = 'Value amount must be greater or equal to 0'
        errors++
      } else {
        this.errs.value_amount = ''
      }
      if(errors > 0){
        return false
      }
      return true
    },
    async savePromoCode(item){
      if(!this.isPromoValid() || this.validateDateRange() > 0){
        return
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos/' + item.id
      let data = this.promo
      data.games = data.purchasable_items
      data.quantity = data.is_unlimited == true ? '-1' : data.quantity
      data.event_start_time = data.event_start_time.getHours() + ':' + data.event_start_time.getMinutes() + ':00'
      data.event_end_time = data.event_end_time.getHours() + ':' + data.event_end_time.getMinutes() + ':00'
      data.groups = this.selectedGroups
      data.rules = this.promoRules

      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code has been updated', 'success')
          this.edit_promo_code = false
          this.view_only = false
          this.getPromos(this.perPage, this.getOffset(this.currentPage,  this.perPage), this.archived)
          this.selectedGroups = []
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async editPromoCode(item, event){
      event.preventDefault()
      this.view_only = false
      this.promo = item
      this.promo.redemption_type = {id: item.redemption_type}
      this.promo.value_type = {id: item.value_type}
      this.promo.purchase_all_dates = {id: item.purchase_is_range}
      this.promo.event_all_dates = {id: item.event_is_date_range}
      this.promo.event_all_times = {id: item.event_is_time_range}
      this.promo.internal_only = Boolean(this.promo.internal_only)
      this.promo.is_combineable = Boolean(this.promo.is_combineable)
      this.promo.is_cross_group = Boolean(this.promo.is_cross_group)
      this.promo.purchasable_items = JSON.parse(item.linked_games).concat(JSON.parse(item.linked_items)).concat(JSON.parse(item.linked_gift_card_types))


      let dt = new Date()
      if(!(this.promo.event_start_time instanceof Date)){
        dt.setHours(this.promo.event_start_time.split(':')[0])
        dt.setMinutes(this.promo.event_start_time.split(':')[1])
        this.promo.event_start_time = dt
      }
      if(!(this.promo.event_end_time instanceof Date)){
        dt = new Date()
        dt.setHours(this.promo.event_end_time.split(':')[0])
        dt.setMinutes(this.promo.event_end_time.split(':')[1])
        this.promo.event_end_time = dt
      }
      if(item.event_exlusions && !Array.isArray(item.event_exlusions)){
        let exl = []
        let obj = JSON.parse(item.event_exlusions)

        for(let n in obj){
          exl.push(obj[n].exclusion_date)
        }
        item.event_exlusions = exl
      }

      if(this.promo.quantity.toString() === '-1'){

        this.promo.is_unlimited = true
        this.un_key++
      } else {
        this.promo.is_unlimited = false
        this.un_key++
      }
      await this.getGroups()
      await this.getAvailablePromoCodeGroups()

      // Load rules for this promo code
      this.promoRules = item.rules || []

      // Reset apply template toggle and store original settings for potential revert
      this.applyTemplateOnEdit = false
      this.originalPromoSettings = JSON.parse(JSON.stringify(this.promo))

      this.edit_promo_code = true
    },
    async createPromoCode(){
      if(!this.isPromoValid() || this.validateDateRange() > 0){
        return
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos'
      let data = this.promo
      if(this.promo.is_unlimited){
        data.quantity = '-1'
      }
      data.event_start_time = data.event_start_time.getHours() + ':' + data.event_start_time.getMinutes() + ':00'
      data.event_end_time = data.event_end_time.getHours() + ':' + data.event_end_time.getMinutes() + ':00'
      data.groups = this.selectedGroups
      data.rules = this.promoRules

      this.axios.post(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code has been created', 'success')
          this.add_promo_code = false
          this.getPromos(this.perPage, this.getOffset(this.currentPage,  this.perPage), this.archived)
          this.selectedGroups = []
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })

    },
    async unarchivePromoCodePrompt(item, event){
      event.preventDefault()
      const confirmed = await this.confirmAction({
        title: 'Are you sure?',
        message: 'This action will unarchive the promo code, making it available on purchases.',
        confirmText: 'Unarchive'
      })
      if(confirmed){
        this.restorePromoCode(item)
      }
    },
    async restorePromoCode(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos/' + item.id
      let data = {
        restore: 1
      }

      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code has been unarchived', 'success')
          this.archived = 0
          this.switchTab(0)
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async deletePromoCodePrompt(item, event){
      event.preventDefault()
      const confirmed = await this.confirmAction({
        title: 'Are you sure?',
        message: 'This action will archive the promo code, making it unavailable on purchases.',
        confirmText: 'Archive'
      })
      if(confirmed){
        this.deletePromoCode(item)
      }
    },
    async deletePromoCode(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos/' + item.id
      this.axios.delete(url, config)
        .then(response => {
          this.showAlert('Success', 'Promo code has been archived', 'success')
          this.getPromos(this.perPage, this.getOffset(this.currentPage,  this.perPage), this.archived)
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getAllPromos(config, count){
      let url = ''
      let promises = []
      let csvData = []
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id

      let offset = 0
      while(offset <= this.totalRows){

        url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?count='
          + count + "&offset=" + offset + "&archived=" + encodeURIComponent(this.archived) + '&' + this.promoFilterQuery()
        // Pages load in parallel; keep each in its offset slot so the CSV follows the sort.
        const pageIndex = promises.length
        promises.push(
          axios.get(url, config)
            .then(response =>{
              this.all_promos = _.sortBy(response.data.promos, 'name')
              csvData[pageIndex] = response.data.promos
            })
            .catch(err => {
                console.log(err)
                if (err.response.status == 401) {
                  this.$router.push('/')
                } else {
                  this.showAlert('Error', 'Something went wrong', 'danger')
                }
              }
            )
        )
        offset = offset + count
      }

      await Promise.all(promises)

      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    async downloadCSV(){
      //get the entire data set
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      let count = 5000
      let csvData = await this.getAllPromos(config, count)
      this.loading = false

      // downloadCsv quotes every value, so commas in formatted dates ("November 03, 2025"),
      // quotes and line breaks stay inside their column. Quantity and remaining match the
      // table: a negative quantity is unlimited, remaining never goes below 0.
      const yesNo = v => (v ? 'Yes' : 'No')
      const limited = row => Number(row.quantity) >= 0
      downloadCsv('promo_codes.csv', [
        { label: 'Promo Code', value: row => row.name },
        { label: 'Description', value: row => row.description || '' },
        { label: 'Promo Code Group', value: row => row.template_name || '' },
        { label: 'Redemption Type', value: row => (row.redemption_type === 0 ? 'Percentage' : 'Value') },
        { label: 'Value Type', value: row => (row.value_type === 0 ? 'Per transaction' : (row.value_type === 1 ? 'Per line item' : 'Per quantity')) },
        { label: 'Value Amount', value: row => (row.redemption_type === 0 ? row.value_amount + '%' : this.formatCurrencyValue(row.value_amount)) },
        { label: 'Quantity', value: row => (limited(row) ? row.quantity : 'Unlimited') },
        { label: 'Times Used', value: row => row.used || 0 },
        { label: 'Remaining', value: row => (limited(row) ? Math.max(0, row.quantity - (row.used || 0)) : 'Unlimited') },
        { label: 'Internal Only', value: row => yesNo(row.internal_only) },
        { label: 'Combineable', value: row => yesNo(row.is_combineable) },
        { label: 'Purchase Date Range', value: row => yesNo(row.purchase_is_range) },
        { label: 'Purchase Start Date', value: row => (row.purchase_start_date ? this.getDate(row.purchase_start_date) : '') },
        { label: 'Purchase End Date', value: row => (row.purchase_end_date ? this.getDate(row.purchase_end_date) : '') },
        { label: 'Event Date Range', value: row => yesNo(row.event_is_date_range) },
        { label: 'Event Start Date', value: row => (row.event_start_date ? this.getDate(row.event_start_date) : '') },
        { label: 'Event End Date', value: row => (row.event_end_date ? this.getDate(row.event_end_date) : '') },
        { label: 'Event Time Range', value: row => yesNo(row.event_is_time_range) },
        { label: 'Event Start Time', value: row => (row.event_start_time ? this.getTime(row.event_start_time) : '') },
        { label: 'Event End Time', value: row => (row.event_end_time ? this.getTime(row.event_end_time) : '') },
        { label: 'Mon', value: row => yesNo(row.available_monday) },
        { label: 'Tue', value: row => yesNo(row.available_tuesday) },
        { label: 'Wed', value: row => yesNo(row.available_wednesday) },
        { label: 'Thu', value: row => yesNo(row.available_thursday) },
        { label: 'Fri', value: row => yesNo(row.available_friday) },
        { label: 'Sat', value: row => yesNo(row.available_saturday) },
        { label: 'Sun', value: row => yesNo(row.available_sunday) }
      ], csvData)
    },
    async addNewPromo(){
      this.promo = {
        name: '',
        is_unlimited: true,
        description: '',
        template_id: null,
        redemption_type: {name:'Percentage discount', id: 0},
        value_amount: '1',
        quantity: '10',
        value_type: {name: 'Per transaction', id: 0},
        games: [],
        groups: [],
        purchase_all_dates: {id: 0, name: 'Available any dates'},
        purchase_all_times: {id: 0, name: 'Available any times'},
        purchase_days: [],
        purchase_exlusions: [],
        purchase_start_date: new Date(),
        purchase_end_date: new Date(),
        event_all_dates: {id: 0, name: 'Available any dates'},
        event_all_times: {id: 0, name: 'Available any times'},
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        event_exlusions: [],
        event_start_date: new Date(),
        event_end_date: new Date(),
        event_start_time: new Date(),
        event_end_time: new Date(),
        is_combineable: false,
        applicable_to_misc_items: 1,
        is_cross_group: false
      }
      this.promoRules = []
      await this.getGroups()
      await this.getAvailablePromoCodeGroups()
      this.applyTemplateOnEdit = false
      this.add_promo_code = true
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    switchTab(index){
      this.activeTabIndex = index
      if(this.activeTabIndex === 0){
        this.archived = 0
        this.currentPage = 1
        this.getAvailablePromoCodeGroups()
      } else if(this.activeTabIndex === 1){
        this.archived = 1
        this.currentPage = 1
        this.edit_promo_code = false
        this.view_only = false
        this.add_promo_code = false
        this.getAvailablePromoCodeGroups()
      }
      this.getPromos(this.perPage, this.getOffset(this.currentPage,  this.perPage), this.archived)
    },
    async getPromos(count, offset, archived, source){
      if(source === 'filter'){
        this.currentPage = 1
      }
      // Only the newest request may update the list: an older response that lands
      // last (a quick second header click) is dropped.
      const seq = ++this._promoFetchSeq

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?' +
        'count=' + count + '&offset=' + offset + '&archived=' + archived + '&' + this.promoFilterQuery()
      this.loading = true
      axios.get(url, config)
        .then((res) => {
          if(seq !== this._promoFetchSeq) return
          this.promo_codes = res.data.promos
          this.totalRows = res.data.totalRows
          this.purchasable_items = res.data.purchasable_items
          this.availableRuleTypes = res.data.rule_types || []
          this.loading = false
          for(let n in this.promo_codes){
            if(this.promo_codes[n].games){
              this.promo_codes[n].games = JSON.parse(this.promo_codes[n].games)
            }
            // if(this.promo_codes[n].event_exlusions){
            //   this.promo_codes[n].event_exlusions = JSON.parse(this.promo_codes[n].event_exlusions)
            // }
          }
          this.addQueryParameters()
        })
        .catch((err) => {
          if(seq !== this._promoFetchSeq) return
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    showAlert(title, message, variant = 'info', onConfirm = null){
      this.confirmationDialog = {
        show: true, title, message, variant,
        showCancel: false, confirmText: 'OK', confirmVariant: 'primary', onConfirm, onCancel: null
      }
    },
    // Resolves true on confirm and false on cancel, ESC or an outside click.
    confirmAction({ title, message, variant = 'warning', confirmText = 'Confirm', confirmVariant = 'primary' }){
      return new Promise(resolve => {
        this.confirmationDialog = {
          show: true, title, message, variant,
          showCancel: true, confirmText, confirmVariant,
          onConfirm: () => resolve(true), onCancel: () => resolve(false)
        }
      })
    },
    confirmationDialogConfirm(){
      const action = this.confirmationDialog.onConfirm
      this.confirmationDialog.show = false
      if(action){
        action()
      }
    },
    confirmationDialogCancel(){
      const action = this.confirmationDialog.onCancel
      this.confirmationDialog.show = false
      if(action){
        action()
      }
    },
    getOffset(currentPage, perPage){
      return Number((currentPage * perPage) - perPage)
    },
    addQueryParameters() {
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        name: this.filterValues.name,
        template_name: this.filterValues.template_name.join(','),
        archived: this.archived,
        sort: this.sort.key,
        dir: this.sort.dir,
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
  },
  created() {
    this._promoFetchSeq = 0
    // DataTable hides its headers at 767px, so phones sort from the filter bar chip.
    this._mobileMql = window.matchMedia('(max-width: 767px)')
    this._onMobileMql = () => { this.isMobile = this._mobileMql.matches }
    this._onMobileMql()
    this._mobileMql.addEventListener('change', this._onMobileMql)
  },
  beforeDestroy() {
    this._mobileMql.removeEventListener('change', this._onMobileMql)
  },
  mounted() {
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    // Default when the query omits count/page/archived, otherwise Number(undefined) => NaN.
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.archived = Number(this.$route.query.archived) || 0
    const urlSort = this.$route.query.sort
    if(urlSort && this.sortOptions.some(o => o.key === urlSort)){
      this.sort = { key: urlSort, dir: this.$route.query.dir === 'asc' ? 'asc' : 'desc' }
    }
    // Set the initial tab from the URL WITHOUT fetching (FilterBar drives the first load
    // via its initial change). switchTab is reserved for user-initiated tab clicks.
    const initialTab = this.$route.query.tab !== undefined ? Number(this.$route.query.tab) : (this.archived ? 1 : 0)
    this.activeTabIndex = initialTab
    // Seed FilterBar's flat values from the URL; FilterBar resolves saved-views precedence
    // (?view= > URL > default > last-used) and drives the first fetch via onFiltersChanged.
    const q = this.$route.query || {}
    this.filterValues = {
      ...emptyPromoFilters(),
      name: q.name || '',
      template_name: q.template_name ? q.template_name.split(',').map(id => parseInt(id)) : []
    }
    this.loading = true
    this.getAvailablePromoCodeGroups()
  }
}
</script>

<style scoped>
/* Tab rail header: the hairline lives on the flex container so it runs
   under the right-side actions too; the rail's own hairline is hidden */
.tabs-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.tabs-header ::v-deep .tab-rail {
  border-bottom-color: transparent;
  min-width: 0;
}
.tabs-header__action {
  margin-top: 0;
  margin-bottom: 14px;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.white-content .tabs-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}

/* Disabled-but-visible dropdown action (e.g. apply group settings when the group has no defaults) */
.dropdown-item-na {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Form sections */
.form-section {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: rgba(128, 128, 128, 0.05);
  border-radius: 0.375rem;
  border-left: 3px solid #e14eca;
}

.form-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem 0;
}

.fit-content {
  width: fit-content;
}

/* Days of week selector */
.days-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0.5rem 0.75rem;
  background: rgba(128, 128, 128, 0.1);
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  user-select: none;
}

.day-pill:hover {
  background: rgba(128, 128, 128, 0.18);
  border-color: rgba(128, 128, 128, 0.35);
}

.day-pill.is-selected {
  background: rgba(225, 78, 202, 0.2);
  border-color: #e14eca;
  color: #e14eca;
}

/* Quick actions */
.quick-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.quick-actions-label {
  font-size: 0.8125rem;
  color: white;
}

/* Amount input group */
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
}

.amount-input-group:focus-within {
  border-color: #e14eca;
}

.amount-input-group.has-error {
  border-color: #ec250d;
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;
}

.amount-input-wrapper :deep(.form-control) {
  border: none;
  border-radius: 0;
  background: transparent;
}

.amount-input-wrapper :deep(.el-input-number) {
  width: 100%;
}

.amount-input-wrapper :deep(.el-input-number .el-input__inner) {
  text-align: left;
  color: white;
  background: transparent;
  border: none;
}

/* Quantity control */
.quantity-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.quantity-toggle {
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  height: 40px;
}

.quantity-option {
  padding: 0 0.875rem;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  height: 100%;
}

.quantity-option:first-child {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.quantity-option:hover:not(.is-active) {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.quantity-option.is-active {
  background: rgba(225, 78, 202, 0.25);
  color: #e14eca;
}

.quantity-input-wrapper {
  display: flex;
  align-items: center;
}

.quantity-input-wrapper :deep(.el-input-number .el-input__inner) {
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-section {
    padding: 1rem;
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }

  .days-selector {
    gap: 0.375rem;
  }

  .day-pill {
    min-width: 40px;
    padding: 0.375rem 0.5rem;
    font-size: 0.8125rem;
  }

  .quick-actions {
    gap: 0.5rem;
  }

  .quantity-control {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .quantity-option {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .row.align-items-end > .col-auto + .col-auto {
    margin-top: 1rem;
  }
}

</style>

<style>
/* Light mode support - unscoped for .white-content parent class */
.white-content .amount-input-group {
  background: #ffffff !important;
  border-color: rgba(225, 78, 202, 0.4) !important;
}

.white-content .amount-prefix {
  background: rgba(225, 78, 202, 0.1) !important;
}

.white-content .amount-input-wrapper .el-input-number .el-input__inner {
  color: #344675 !important;
}

.white-content .quantity-toggle {
  border-color: rgba(0, 0, 0, 0.15) !important;
  background: #ffffff !important;
}

.white-content .quantity-option {
  color: #525f7f !important;
}

.white-content .quantity-option:first-child {
  border-right-color: rgba(0, 0, 0, 0.15) !important;
}

.white-content .quantity-option:hover:not(.is-active) {
  background: rgba(0, 0, 0, 0.05) !important;
  color: #344675 !important;
}

.white-content .quantity-option.is-active {
  background: rgba(225, 78, 202, 0.15) !important;
  color: #e14eca !important;
}

.white-content .quantity-input-wrapper .el-input-number .el-input__inner {
  color: #344675 !important;
}

.white-content .quick-actions-label {
  color: #525f7f !important;
}

.recommended-badge {
  font-size: 10px;
  font-weight: 500;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.12);
  border-radius: 3px;
  padding: 1px 6px;
  margin-left: 8px;
  letter-spacing: 0.3px;
}
</style>
